import 'emoji-picker-element';
import './styles.css';
import { renderBusinessHours, BusinessHoursDisplay } from './business-hours';
import { SocketManager, SocketMessage, ConversationData, TranslationUpdate, LanguageVariant } from './socket-manager.js';
import { ReadReceiptTracker } from './read-receipt-tracker.js';
import { getTranslations, t, WidgetTranslations } from './translations.js';
import { applyTextDirection } from './text-direction.js';
import { VisitorTracker, PopupConfig, FlowNode, DisplayFlow } from './visitor-tracker.js';
import { PageTracker } from './page-tracker.js';
import {
  FilePasteHandler,
  type AttachmentInfo,
  type PendingUpload,
  type UploadAttachmentResponse,
  type UploadFailure,
  type UploadSuccess,
} from './file-paste-handler.js';

// Re-export LanguageVariant for external use
export type { LanguageVariant };

/**
 * Detect which language variant matches the current URL.
 * Checks path prefixes (/en, /pl) and subdomains (en., pl.)
 *
 * @param currentUrl - The URL to match against variant patterns
 * @param variants - Array of language variants with url_patterns
 * @returns The matched variant, or the default variant, or null if no match
 *
 * @example
 * // Path prefix matching
 * detectLanguageVariant('https://shop.com/en/products', variants)
 * // Returns variant with url_patterns containing '/en'
 *
 * @example
 * // Subdomain matching
 * detectLanguageVariant('https://en.shop.com/products', variants)
 * // Returns variant with url_patterns containing 'en.'
 */
export function detectLanguageVariant(
  currentUrl: string,
  variants: LanguageVariant[]
): LanguageVariant | null {
  if (!variants || variants.length === 0) {
    return null;
  }

  try {
    const url = new URL(currentUrl);
    const hostname = url.hostname;
    const pathname = url.pathname;

    for (const variant of variants) {
      if (!variant.url_patterns) continue;

      for (const pattern of variant.url_patterns) {
        // Subdomain pattern: ends with "." (e.g., "en.")
        if (pattern.endsWith('.') && hostname.startsWith(pattern)) {
          return variant;
        }

        // Path pattern: starts with "/" (e.g., "/en")
        if (pattern.startsWith('/')) {
          // Match exact path or path with trailing content
          if (pathname === pattern || pathname.startsWith(pattern + '/')) {
            return variant;
          }
        }
      }
    }
  } catch (error) {
    console.warn('[Widget] Failed to parse URL for language detection:', error);
  }

  // Fallback: return variant with is_default=true
  return variants.find(v => v.is_default) || null;
}

export interface WidgetConfig {
  apiUrl: string;
  siteId: string;
  widgetKey: string;
  containerId?: string;
  theme?: 'light' | 'dark';
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  translationEnabled?: boolean;
  customerId?: string;
  customerEmail?: string;
  customerName?: string;
  customerType?: string;
  companyName?: string;
  companyId?: string;
  welcomeMessage?: string;
  headerTitle?: string;
  inputPlaceholder?: string;
  openOn?: 'click' | 'hover';
  debug?: boolean;
}

/**
 * New simplified Quantumchat Widget with clean WebSocket communication
 * Rebuilt from scratch with focus on:
 * - Clean separation of concerns
 * - Reliable real-time communication
 * - Easy debugging and maintenance
 * - Immediate translation updates
 */
export class QuantumchatWidget {
  private config: WidgetConfig;
  private socketManager: SocketManager;
  private readReceiptTracker: ReadReceiptTracker | null = null;
  private container: HTMLElement | null = null;
  private conversationId: string | null = null;
  private guestId: string | undefined;
  private storageKey: string;
  private guestStorageKey: string;
  private isOpen: boolean = false;
  private messages: SocketMessage[] = [];
  private unreadCount: number = 0;
  private hasLoadedHistory: boolean = false;
  private hasMore: boolean = true;
  private oldestMessageCursor?: string;
  private isLoadingOlderMessages: boolean = false;
  private isOfflineFormShown: boolean = false;
  private businessHoursDisplay: BusinessHoursDisplay | null = null;
  private translations: WidgetTranslations;
  private currentLocale: string = 'en';
  private detectedVariant: LanguageVariant | null = null;

  // Popup tracking
  private visitorTracker: VisitorTracker | null = null;
  private pageTracker: PageTracker | null = null;
  private popups: PopupConfig[] = [];
  private activePopup: PopupConfig | null = null;
  private popupContainer: HTMLElement | null = null;

  // Trigger state tracking
  private triggerTimers: Map<string, number> = new Map();
  private scrollListenerActive: boolean = false;
  private exitIntentListenerActive: boolean = false;
  private triggeredPopups: Set<string> = new Set();

  // Typing preview state
  private typingPreviewEnabled: boolean = false;
  private lastSentPreviewText: string = '';
  private previewDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private onlineAgents: Array<{ id: number; name: string; avatar_url?: string | null; initials?: { letters: string; color: string } }> = [];
  private assignedAgent: { agent_id: number; name: string; avatar_url?: string | null; initials?: { letters: string; color: string } } | null = null;
  private offlineFormTimer: ReturnType<typeof setTimeout> | null = null;
  private hoverOpenEnabled: boolean = false;
  private hoverOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private hoverOpenedAt: number = 0;
  private hoverEnterListener: ((e: PointerEvent) => void) | null = null;
  private hoverLeaveListener: (() => void) | null = null;
  private hoverClickGuard: ((e: MouseEvent) => void) | null = null;
  private pasteHandler: FilePasteHandler | null = null;
  private isSendingAttachments: boolean = false;

  constructor(config: WidgetConfig) {
    this.config = {
      theme: 'light',
      position: 'bottom-right',
      primaryColor: '#990000',
      translationEnabled: true,
      debug: false,
      headerTitle: 'Customer Support',
      inputPlaceholder: 'Type your message...',
      ...config
    };

    // Initialize translations with 'en' as default - will be updated from server's site_config
    this.translations = getTranslations('en');

    // Initialize storage keys (locale not included - conversations persist regardless of language)
    this.storageKey = `quantumchat_conversation_${this.config.siteId}`;
    this.guestStorageKey = `quantumchat_guest_${this.config.siteId}`;

    // Load or generate guest ID (persistent across sessions)
    this.loadOrGenerateGuestId();

    // Load existing conversation from localStorage
    this.loadConversationFromStorage();

    // Detect browser language for auto-translations
    const browserLanguage = this.detectBrowserLanguage();

    // Initialize socket manager
    // Note: browser_language is sent for auto-translation feature
    this.socketManager = new SocketManager({
      apiUrl: this.config.apiUrl,
      siteId: this.config.siteId,
      userType: 'customer',
      customerId: this.config.customerId,
      customerEmail: this.config.customerEmail,
      customerName: this.config.customerName,
      customerType: this.config.customerType,
      companyName: this.config.companyName,
      companyId: this.config.companyId,
      guestId: this.guestId, // Add guest ID for anonymous tracking
      widgetKey: this.config.widgetKey,
      browserLanguage: browserLanguage, // For auto-translation feature
      debug: this.config.debug
    });

    // Set up socket event handlers
    this.setupSocketEventHandlers();

    // Initialize visitor tracker for popup display rules
    this.visitorTracker = new VisitorTracker(this.config.siteId, this.config.debug || false);

    // DEBUG: Make widget accessible from browser console
    (window as any).quantumchatWidgetV2 = this;
    this.log('Widget V2 initialized, accessible via window.quantumchatWidgetV2');
  }

  /**
   * Initialize the widget
   */
  public async init(): Promise<void> {
    try {
      this.log('🚀 Initializing widget...');

      // Check for conversation ID in URL (from email links)
      const urlConvId = this.getConversationIdFromUrl();
      if (urlConvId) {
        this.log('📧 Found conversation ID from URL:', urlConvId);
        this.conversationId = urlConvId;
        this.saveConversationToStorage();
      }

      // Create UI container
      this.createContainer();
      this.setupEventListeners();

      // Connect to WebSocket
      await this.socketManager.connect();

      // Initialize read receipt tracker after connection
      // Pass isOpen callback to ensure messages are only marked as read when widget is actually open
      this.readReceiptTracker = new ReadReceiptTracker(
        this.socketManager,
        this.config.debug || false,
        () => this.isOpen // Check if widget chat panel is open
      );

      // Set the messages container for intersection observer
      const messagesContainer = this.container?.querySelector('#quantumchat-messages') as HTMLElement;
      if (messagesContainer && this.readReceiptTracker) {
        this.readReceiptTracker.setMessagesContainer(messagesContainer);
      }

      // Set conversation ID if we have one from storage
      if (this.conversationId) {
        console.log('🔄 [Widget.init] Found stored conversation ID:', this.conversationId);
        console.log('🔄 [Widget.init] Calling socketManager.setConversationId and joinConversation...');
        this.socketManager.setConversationId(this.conversationId);
        this.socketManager.joinConversation(this.conversationId);
        console.log('✅ [Widget.init] joinConversation called');
      } else {
        console.log('ℹ️ [Widget.init] No stored conversation ID found');
      }
      // Note: No longer creating conversation automatically
      // Conversation will be created when user sends first message

      // Auto-open widget if conversation was loaded from URL
      if (urlConvId) {
        this.log('📧 Auto-opening widget for email link conversation');
        setTimeout(() => this.open(), 500);
        // Clean up URL parameter
        this.cleanUrlParameter();
      }

      // Fetch and evaluate popups
      this.fetchAndEvaluatePopups();

      this.log('✅ Widget initialized successfully');
    } catch (error) {
      this.log('❌ Widget initialization failed:', error);
      throw error;
    }
  }

  /**
   * Get conversation ID from URL parameter (for email reply links)
   */
  private getConversationIdFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('qc_conv');
  }

  /**
   * Remove qc_conv parameter from URL without page reload
   */
  private cleanUrlParameter(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('qc_conv');
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Set up socket event handlers
   */
  private setupSocketEventHandlers(): void {
    this.socketManager.onConnectionStateChange = (connected: boolean) => {
      this.log('🔌 Connection state changed:', connected);
      this.updateConnectionIndicator(connected);
    };

    this.socketManager.onConversationCreated = (data: ConversationData) => {
      this.log('✅ Conversation created:', data);
      this.conversationId = data.conversationId;
      this.hasLoadedHistory = true;
      this.saveConversationToStorage();

      // Set assigned agent from conversation data if present
      this.setAssignedAgentFromConversation(data);

      // Don't render messages if offline form is shown (showing success message instead)
      if (this.isOfflineFormShown) {
        this.log('📋 Offline form shown, skipping message rendering');
        return;
      }

      if (data.messages && data.messages.length > 0) {
        // Process messages to use translations for agent messages
        this.messages = this.processMessagesWithTranslations(data.messages);
        this.renderMessages();
      }
    };

    this.socketManager.onConversationRestored = (data: ConversationData) => {
      console.log('🔄 [Widget] onConversationRestored called with data:', data);
      console.log('🔍 [Widget] Checking conversation status:', {
        status: data.status,
        isClosed: data.status === 'closed',
        conversationId: data.conversationId
      });
      this.log('🔄 Conversation restored:', data);

      // Don't render messages if offline form is shown (showing success message instead)
      if (this.isOfflineFormShown) {
        this.log('📋 Offline form shown, skipping conversation restore rendering');
        return;
      }

      // Check if conversation is closed - don't restore closed conversations
      if (data.status === 'closed') {
        console.log('❌ [Widget] CONVERSATION IS CLOSED - will clear and start fresh');
        this.log('⚠️ Conversation is closed, clearing and starting fresh');
        this.clearConversationFromStorage();
        this.conversationId = null;
        this.messages = [];
        this.hasLoadedHistory = false;
        this.assignedAgent = null; // Clear assigned agent
        this.renderMessages(); // Will show welcomeMessage since messages is empty
        console.log('✅ [Widget] Cleared closed conversation, will show welcome message');
        return;
      }
      console.log('✅ [Widget] Conversation is active, will restore normally');

      // Set assigned agent from conversation data if present
      this.setAssignedAgentFromConversation(data);

      this.hasLoadedHistory = true;

      if (data.messages && data.messages.length > 0) {
        // Process messages to use translations for agent messages
        this.messages = this.processMessagesWithTranslations(data.messages);
        this.hasMore = data.hasMore;
        this.oldestMessageCursor = data.nextCursor;
        this.renderMessages();
      }
    };

    this.socketManager.onMessageReceived = (message: SocketMessage) => {
      this.log('📨 New message received:', message);
      this.addMessage(message);
    };

    this.socketManager.onTranslationUpdated = (update: TranslationUpdate) => {
      this.log('🌍 Translation updated:', update);
      this.handleTranslationUpdate(update);
    };

    this.socketManager.onTranslationDeleted = (data) => {
      this.log('🗑️ Translation deleted:', data);
      this.handleTranslationDelete(data);
    };

    this.socketManager.onContentUpdated = (data) => {
      this.log('✏️ Content updated:', data);
      this.handleContentUpdate(data);
    };

    this.socketManager.onError = (error: any) => {
      this.log('❌ Socket error:', error);

      // If error mentions conversation not found or failed to restore, clear localStorage and create new one
      // Check both error.message and error.reason (Phoenix channels use 'reason')
      const errorMsg = error.message || error.reason || '';
      const isConversationError = errorMsg.includes('not found') ||
                                  errorMsg.includes('Failed to restore conversation') ||
                                  errorMsg.includes('conversation not found');

      if (isConversationError && this.conversationId) {
        console.log('❌ Conversation error, clearing localStorage for conversation:', this.conversationId);
        this.clearConversationFromStorage();
        this.conversationId = null;
        this.socketManager.setConversationId(null as any);
        this.messages = [];
        this.hasLoadedHistory = false;
        this.renderMessages(); // Will show welcome message

        console.log('✅ Cleared invalid conversation, widget ready for new conversation');
      } else {
        this.showErrorMessage('Connection error occurred');
      }
    };

    // Handler for agents offline - debounce before showing the form to avoid flicker
    this.socketManager.onAgentsOffline = (data) => {
      this.log('🔴 All agents went offline:', data);
      this.onlineAgents = [];
      this.renderHeaderAvatars();
      if (this.offlineFormTimer) {
        clearTimeout(this.offlineFormTimer);
      }
      this.offlineFormTimer = setTimeout(() => {
        this.showOfflineForm();
      }, 800);
    };

    // Handler for agent available
    this.socketManager.onAgentAvailable = (data) => {
      this.log('🟢 Agent is now available:', data);
      if ((data as any)?.agents) {
        this.updateOnlineAgents((data as any).agents);
      }
      if (this.offlineFormTimer) {
        clearTimeout(this.offlineFormTimer);
        this.offlineFormTimer = null;
      }
      if (this.isOfflineFormShown) {
        this.hideOfflineForm();
      }
    };

    // Handler for agent assigned to conversation
    this.socketManager.onAgentAssigned = (data) => {
      this.log('👤 Agent assigned to conversation:', data);
      this.assignedAgent = data;
      this.updateHeaderWithAssignedAgent();
    };

    this.socketManager.onConversationChannelJoined = () => {
      this.pageTracker?.emitCurrentPage();
    };

    // Handler for site config received (sets locale from server - this is the authoritative source)
    this.socketManager.onSiteConfigReceived = (siteConfig) => {
      console.log('📦 [Widget] Site config received:', siteConfig);
      this.log('📦 Site config received:', siteConfig);

      // Detect language variant from URL
      if (siteConfig.language_variants && siteConfig.language_variants.length > 0) {
        this.detectedVariant = detectLanguageVariant(window.location.href, siteConfig.language_variants);
        this.log('🌐 Detected language variant:', this.detectedVariant?.language_code || 'none (using defaults)');
      }

      // Handle auto-translation status (browser language detection)
      if (siteConfig.auto_translation_status) {
        const status = siteConfig.auto_translation_status;
        this.log('🌐 Auto-translation status:', status);

        if (status.status === 'ready' && status.variant) {
          // Translations ready - apply them immediately
          this.log('🌐 Auto-translations ready, applying variant');
          this.detectedVariant = status.variant;
          this.applyAutoTranslations(status.variant);
        } else if (status.status === 'generating') {
          // Translations being generated - will receive via translations:ready event
          this.log('🌐 Auto-translations generating, waiting for ready event');
        } else if (status.status === 'fallback') {
          // Cannot generate - use default
          this.log('🌐 Auto-translations fallback:', status.reason);
        }
      }

      // Determine locale and translations - variant takes precedence
      const langCode = this.detectedVariant?.language_code || siteConfig.widget_language || 'en';
      this.log(`🌐 Setting locale to '${langCode}' ${this.detectedVariant ? 'from detected variant' : 'from server'}`);
      this.currentLocale = langCode;
      if (this.container) applyTextDirection(this.container, this.currentLocale);

      // Get built-in translations, then override with variant texts
      this.translations = this.getTranslationsWithVariant(
        this.detectedVariant,
        getTranslations(langCode)
      );

      // Update UI elements with variant values
      this.updateUIWithVariant(siteConfig.welcome_message);

      // Apply widget open trigger from server config
      if (siteConfig.widget_open_on === 'click' || siteConfig.widget_open_on === 'hover') {
        this.hoverOpenEnabled = siteConfig.widget_open_on === 'hover';
        this.log(`🖱️ Widget open mode: ${siteConfig.widget_open_on}`);
      }

      // Store typing preview setting
      console.log('⌨️  [Widget] typing_preview_enabled in config:', (siteConfig as any).typing_preview_enabled);
      if ((siteConfig as any).typing_preview_enabled !== undefined) {
        this.typingPreviewEnabled = (siteConfig as any).typing_preview_enabled;
        console.log(`⌨️  [Widget] Typing preview ${this.typingPreviewEnabled ? 'ENABLED' : 'DISABLED'}`);
        this.log(`⌨️  Typing preview ${this.typingPreviewEnabled ? 'enabled' : 'disabled'}`);
      }

      this.businessHoursDisplay =
        (siteConfig as any).business_hours_display || null;

      const pageTrackingEnabled = (siteConfig as any).page_tracking_enabled !== false;
      this.socketManager.setPageTrackingEnabled(pageTrackingEnabled);
      this.pageTracker?.stop();
      this.pageTracker = null;

      if (pageTrackingEnabled) {
        this.pageTracker = new PageTracker({
          privacyPatterns: (siteConfig as any).privacy_patterns || [],
          privacyParamBlacklist: (siteConfig as any).privacy_param_blacklist || [],
          push: (payload) => {
            this.socketManager.emitSiteView(window.location.href);
            return this.socketManager.pushPageChanged(payload);
          },
          debug: this.config.debug || false,
        });
        this.pageTracker.start();
        this.socketManager.emitSiteView(window.location.href);
      }
    };

    // Handler for auto-generated translations ready (received via PubSub)
    this.socketManager.onAutoTranslationReady = (data) => {
      this.log('🌐 Auto-translation ready event received:', data);
      if (data.translations) {
        this.detectedVariant = data.translations;
        this.applyAutoTranslations(data.translations);
      }
    };
  }

  /**
   * Merge language variant texts with built-in translations.
   * Variant texts override built-in translations only when present (non-empty).
   */
  private getTranslationsWithVariant(
    variant: LanguageVariant | null,
    builtIn: WidgetTranslations
  ): WidgetTranslations {
    if (!variant) return builtIn;

    return {
      ...builtIn,
      // Override only non-empty values from variant
      ...(variant.offline_title && { offline_title: variant.offline_title }),
      ...(variant.offline_subtitle && { offline_subtitle: variant.offline_subtitle }),
      ...(variant.email_label && { email_label: variant.email_label }),
      ...(variant.email_placeholder && { email_placeholder: variant.email_placeholder }),
      ...(variant.name_label && { name_label: variant.name_label }),
      ...(variant.name_placeholder && { name_placeholder: variant.name_placeholder }),
      ...(variant.message_label && { message_label: variant.message_label }),
      ...(variant.message_placeholder && { message_placeholder: variant.message_placeholder }),
      ...(variant.submit_button && { submit_button: variant.submit_button }),
      ...(variant.success_title && { success_title: variant.success_title }),
      ...(variant.success_message && { success_message: variant.success_message }),
      ...(variant.bh_next_opening && { bh_next_opening: variant.bh_next_opening }),
      ...(variant.bh_next_opening_local && { bh_next_opening_local: variant.bh_next_opening_local }),
      ...(variant.bh_hours_label && { bh_hours_label: variant.bh_hours_label }),
      ...(variant.bh_closed && { bh_closed: variant.bh_closed }),
      ...(variant.bh_timezone_note && { bh_timezone_note: variant.bh_timezone_note }),
    };
  }

  /**
   * Update UI elements with language variant values.
   * Called after site config is received to update header, input, and welcome message.
   */
  private updateUIWithVariant(defaultWelcomeMessage?: string): void {
    // Update header title
    const headerTitle = this.detectedVariant?.header_title || this.config.headerTitle || 'Customer Support';
    const agentNameEl = this.container?.querySelector('.agent-name') as HTMLElement;
    if (agentNameEl) {
      agentNameEl.textContent = headerTitle;
    }

    // Update input placeholder
    const inputPlaceholder = this.detectedVariant?.input_placeholder || this.config.inputPlaceholder || this.translations.message_placeholder;
    const inputEl = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
    if (inputEl && inputPlaceholder) {
      inputEl.placeholder = inputPlaceholder;
    }

    // Store welcome message for later use (when no messages exist)
    // Variant's welcome_message takes precedence, then site config, then config
    const welcomeMessage = this.detectedVariant?.welcome_message || defaultWelcomeMessage || this.config.welcomeMessage;
    if (welcomeMessage) {
      this.config.welcomeMessage = welcomeMessage;
    }

    // Re-render messages if empty (to update welcome message)
    if (this.messages.length === 0 && !this.isOfflineFormShown) {
      this.renderMessages();
    }
  }

  /**
   * Process messages to use translations for agent messages
   */
  private processMessagesWithTranslations(messages: SocketMessage[]): SocketMessage[] {
    return messages.map(message => {
      console.log('🔍 [processMessages] Processing message:', {
        id: message.id,
        author: message.author,
        text: message.text,
        body_src: message.body_src,
        hasTranslation: !!message.translation,
        translation: message.translation
      });

      // Store original text
      if (!message.originalText) {
        message.originalText = message.text;
      }

      // For agent messages, use translation if available
      if (message.author === 'agent' && message.translation?.translatedText) {
        console.log('✅ [processMessages] Using translation for agent message:', message.id, message.translation.translatedText);
        message.text = message.translation.translatedText;
        this.log('🌍 Using translated text for agent message:', message.id, message.text);
      } else if (message.author === 'agent') {
        console.log('⚠️  [processMessages] Agent message has NO translation:', message.id);
      }

      return message;
    });
  }

  /**
   * Handle real-time translation updates
   */
  private handleTranslationUpdate(update: TranslationUpdate): void {
    this.log('🔄 Handling translation update:', {
      msgId: update.msgId,
      translation: update.translation,
      totalMessages: this.messages.length
    });

    // Find the message to update (try different ID formats)
    let messageIndex = this.messages.findIndex(m =>
      m.id === update.msgId ||
      m.id === String(update.msgId) ||
      String(m.id) === String(update.msgId)
    );

    if (messageIndex === -1) {
      this.log('⚠️ Message not found for translation update. Searching in all messages:', {
        updateMsgId: update.msgId,
        messageIds: this.messages.map(m => ({ id: m.id, author: m.author, text: m.text.substring(0, 20) }))
      });
      return;
    }

    // Update the message with new translation
    const message = this.messages[messageIndex];
    message.translation = update.translation;

    // Clear translation pending flag - translation is now ready
    if (message.translationPending) {
      delete message.translationPending;
      this.log('✅ Cleared translationPending flag for message:', message.id);
    }

    // If this is an agent message, update the displayed text
    if (message.author === 'agent' && update.translation.translatedText) {
      const oldText = message.text;
      message.text = update.translation.translatedText;
      this.log('🌍 Updated agent message text:', {
        id: message.id,
        oldText,
        newText: message.text
      });
    }

    this.log('✅ Applied translation update to message:', message.id);

    // Re-render the specific message
    this.renderMessage(message, messageIndex);
  }

  /**
   * Handle translation deletion
   */
  private handleTranslationDelete(data: { msgId: string; convId: string }): void {
    const messageIndex = this.messages.findIndex(m => m.id === data.msgId);

    if (messageIndex === -1) {
      this.log('⚠️ Message not found for translation delete:', data.msgId);
      return;
    }

    const message = this.messages[messageIndex];

    // Restore original text if we have it
    if (message.originalText) {
      message.text = message.originalText;
    }

    // Remove translation
    delete message.translation;

    this.log('✅ Removed translation from message:', message.id);

    // Re-render the specific message
    this.renderMessage(message, messageIndex);
  }

  /**
   * Handle content update from server (when agent edits a message)
   */
  private handleContentUpdate(data: { msgId: string; messageId: number; convId: string; content: string }): void {
    this.log('🔄 Handling content update:', {
      msgId: data.msgId,
      content: data.content,
      totalMessages: this.messages.length
    });

    // Find the message to update (try different ID formats)
    let messageIndex = this.messages.findIndex(m =>
      m.id === data.msgId ||
      m.id === String(data.msgId) ||
      String(m.id) === String(data.msgId) ||
      m.id === String(data.messageId) ||
      String(m.id) === String(data.messageId)
    );

    if (messageIndex === -1) {
      this.log('⚠️ Message not found for content update:', {
        updateMsgId: data.msgId,
        messageIds: this.messages.map(m => ({ id: m.id, author: m.author, text: m.text.substring(0, 20) }))
      });
      return;
    }

    // Update the message with new content
    const message = this.messages[messageIndex];

    // Always update the original text (body_src)
    message.originalText = data.content;
    message.body_src = data.content;

    // For agent messages with translation: clear translation (it's now outdated)
    // Mark as pending translation - don't show original text to customer
    if (message.author === 'agent') {
      if (message.translation) {
        this.log('⚠️ Clearing outdated translation for edited message:', message.id);
        delete message.translation;
        delete message.body_translated;
      }
      // Mark as pending translation if translations are enabled
      if (this.config.translationEnabled) {
        message.translationPending = true;
        this.log('🔄 Marked message as translation pending:', message.id);
      }
    }

    // Update displayed text - for agent messages with pending translation, keep empty
    // so that renderMessage shows the translation loader
    if (message.author === 'agent' && message.translationPending) {
      message.text = ''; // Will show loader in renderMessage
    } else {
      message.text = data.content;
    }

    this.log('✅ Updated message content:', message.id);

    // Re-render the specific message
    this.renderMessage(message, messageIndex);
  }

  /**
   * Ensure conversation exists before sending messages
   * @param firstMessage - The first message to send (required for creating new conversations)
   * @returns true if a new conversation was created (message already sent), false if conversation already existed
   */
  private async ensureConversation(firstMessage?: string): Promise<boolean> {
    if (this.conversationId) {
      this.log('✅ Conversation already exists:', this.conversationId);
      return false; // Conversation already existed
    }

    this.log('🆕 Creating new conversation for first message...');

    // Create conversation and wait for it to be established
    return new Promise<boolean>((resolve, reject) => {
      // Set up temporary handler for conversation creation
      const originalHandler = this.socketManager.onConversationCreated;

      this.socketManager.onConversationCreated = (data: ConversationData) => {
        this.log('✅ Conversation created for first message:', data);
        this.conversationId = data.conversationId;
        this.hasLoadedHistory = true;
        this.saveConversationToStorage();

        if (data.messages && data.messages.length > 0) {
          // Process messages to use translations for agent messages
          this.messages = this.processMessagesWithTranslations(data.messages);
          this.renderMessages();
        }

        // Restore original handler
        this.socketManager.onConversationCreated = originalHandler;
        resolve(true); // New conversation was created
      };

      // Create the conversation with the first message
      // Backend requires either a message or customerEmail for conversation creation
      // Pass detected language variant to set correct client_language
      this.socketManager.createConversation({
        message: firstMessage,
        client_language: this.detectedVariant?.language_code,
      });

      // Set timeout in case conversation creation fails
      setTimeout(() => {
        this.socketManager.onConversationCreated = originalHandler;
        reject(new Error('Conversation creation timeout'));
      }, 10000);
    });
  }

  /**
   * Send a text message
   */
  public async sendMessage(
    text?: string,
    attachmentIds?: number[],
    attachmentsMetadata?: AttachmentInfo[]
  ): Promise<boolean> {
    const input = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
    if (!input) {
      console.error('❌ [sendMessage] Input element not found!');
      return false;
    }

    const messageText = text || input.value.trim();
    const hasAttachments = Array.isArray(attachmentIds) && attachmentIds.length > 0;

    if (!messageText && !hasAttachments) {
      return false;
    }

    this.log('📤 Sending message:', messageText);
    const tempId = `temp-${Date.now()}`;

    try {
      if (hasAttachments) {
        await this.ensureConversation();

        this.addMessage({
          id: tempId,
          conversationId: this.conversationId || '',
          text: messageText,
          originalText: messageText,
          author: 'customer',
          timestamp: new Date(),
          isTemporary: true,
          attachments: attachmentsMetadata ?? [],
        });

        await this.socketManager.sendMessage(messageText, attachmentIds);
      } else {
        // Pass the message text - if new conversation is created, message is included.
        const isNewConversation = await this.ensureConversation(messageText);

        this.addMessage({
          id: tempId,
          conversationId: this.conversationId || '',
          text: messageText,
          originalText: messageText,
          author: 'customer',
          timestamp: new Date(),
          isTemporary: true
        });

        // Only send via WebSocket if conversation already existed.
        if (!isNewConversation) {
          await this.socketManager.sendMessage(messageText);
        }
      }

      // Clear input
      input.value = '';
      input.style.height = '40px';
      this.updateSendButton();

      // Clear typing preview state
      this.lastSentPreviewText = '';
      if (this.typingPreviewEnabled) {
        this.socketManager.sendTypingPreview('');
      }

      return true;
    } catch (error) {
      this.removeMessage(tempId);
      console.error('❌ [sendMessage] Error:', error);
      this.log('❌ Failed to send message:', error);
      this.showErrorMessage('Failed to send message. Please try again.');
      return false;
    }
  }

  /**
   * Hover-open on the launcher, hardened against accidental opens:
   * - only real mouse pointers (touch/pen taps open via click, otherwise the tap's
   *   compat mouseenter opens and the following click closes the widget again),
   * - the pointer must rest on the launcher for HOVER_OPEN_DELAY_MS (a cursor passing by,
   *   or the breathing animation growing into a resting cursor, must not open it),
   * - for HOVER_CLICK_GUARD_MS after a hover-open, a click outside the widget is swallowed:
   *   the user meant to click the launcher, which is already hidden, so the click would
   *   otherwise hit the host page element underneath.
   */
  private static readonly HOVER_OPEN_DELAY_MS = 300;
  private static readonly HOVER_CLICK_GUARD_MS = 500;

  private setupHoverOpen(launcher: Element | null | undefined): void {
    if (!launcher) return;

    this.hoverEnterListener = (e: PointerEvent) => {
      if (!this.hoverOpenEnabled || this.isOpen || e.pointerType !== 'mouse') return;

      this.cancelHoverOpen();
      this.hoverOpenTimer = setTimeout(() => {
        this.hoverOpenTimer = null;
        if (this.isOpen) return;
        this.open();
        this.hoverOpenedAt = Date.now();
      }, QuantumchatWidget.HOVER_OPEN_DELAY_MS);
    };
    this.hoverLeaveListener = () => this.cancelHoverOpen();

    this.hoverClickGuard = (e: MouseEvent) => {
      if (!this.hoverOpenedAt) return;
      if (Date.now() - this.hoverOpenedAt > QuantumchatWidget.HOVER_CLICK_GUARD_MS) {
        this.hoverOpenedAt = 0;
        return;
      }
      if (this.container?.contains(e.target as Node)) return;

      this.hoverOpenedAt = 0;
      e.preventDefault();
      e.stopPropagation();
      this.log('🖱️ Swallowed click right after hover-open');
    };

    launcher.addEventListener('pointerenter', this.hoverEnterListener as EventListener);
    launcher.addEventListener('pointerleave', this.hoverLeaveListener);
    document.addEventListener('click', this.hoverClickGuard, true);
  }

  private cancelHoverOpen(): void {
    if (this.hoverOpenTimer) {
      clearTimeout(this.hoverOpenTimer);
      this.hoverOpenTimer = null;
    }
  }

  /**
   * Toggle widget open/closed
   */
  public toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the chat widget
   * @param prefilledMessage Optional message to prefill in the input field
   */
  public open(prefilledMessage?: string): void {
    if (this.isOpen) return;

    this.isOpen = true;
    this.cancelHoverOpen();

    if (this.container) {
      const launcher = this.container.querySelector('.quantumchat-launcher');
      const chatWindow = this.container.querySelector('.quantumchat-window');

      if (launcher && chatWindow) {
        launcher.classList.add('hidden');
        chatWindow.classList.add('open');
      }
    }

    this.unreadCount = 0;
    this.updateUnreadCount();
    this.markMessagesAsRead();

    // Notify read receipt tracker that widget is now open
    // This will check and mark visible messages as read after DOM settles
    if (this.readReceiptTracker) {
      this.readReceiptTracker.onWidgetOpened();
    }

    // Focus input field and optionally prefill message
    setTimeout(() => {
      const input = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
      if (input) {
        if (prefilledMessage) {
          input.value = prefilledMessage;
          this.updateSendButton();
          this.autoResize(input);
        }
        input.focus();
      }
    }, 100);

    this.log('📖 Widget opened' + (prefilledMessage ? ` with message: "${prefilledMessage}"` : ''));
  }

  /**
   * Close the chat widget
   */
  public close(): void {
    if (!this.isOpen) return;

    this.isOpen = false;

    if (this.container) {
      const launcher = this.container.querySelector('.quantumchat-launcher');
      const chatWindow = this.container.querySelector('.quantumchat-window');

      if (launcher && chatWindow) {
        launcher.classList.remove('hidden');
        chatWindow.classList.remove('open');
      }
    }

    this.log('📕 Widget closed');
  }

  /**
   * Check if widget is currently open
   */
  public isWidgetOpen(): boolean {
    return this.isOpen;
  }

  /**
   * Add a new message to the list and render it
   */
  private addMessage(message: SocketMessage): void {
    // Don't add messages to UI if offline form is shown
    if (this.isOfflineFormShown) {
      this.log('📋 Offline form shown, skipping addMessage');
      return;
    }

    this.log('📥 Processing new message:', {
      id: message.id,
      text: message.text,
      author: message.author,
      isTemporary: message.isTemporary
    });

    // Process message to use translation for agent messages
    if (!message.originalText) {
      message.originalText = message.text;
    }

    // For agent messages, use translation if available
    if (message.author === 'agent' && message.translation?.translatedText) {
      message.text = message.translation.translatedText;
      this.log('🌍 Using translated text for new agent message:', message.id, message.text);
    }

    // Check if this is a real message that should replace a temporary one
    if (!message.isTemporary && message.author === 'customer') {
      // Find and replace any temporary message with similar text or attachments
      const tempIndex = this.messages.findIndex(m =>
        m.isTemporary &&
        m.author === 'customer' &&
        (
          // Match by text for text messages
          (m.originalText === message.originalText && message.originalText) ||
          // Match by attachment for attachment messages
          ((message.attachments?.length ?? 0) > 0 && (m.attachments?.length ?? 0) > 0 &&
           message.attachments?.[0]?.id === m.attachments?.[0]?.id)
        )
      );

      if (tempIndex !== -1) {
        // Replace temporary message with real one
        this.messages[tempIndex] = message;
        this.renderMessage(message, tempIndex);
        this.scrollToBottom();
        this.log('✅ Replaced temporary message with real one:', message.id);
        return;
      }
    }

    // Avoid duplicates for non-temporary messages
    if (!message.isTemporary && message.id && this.messages.find(m => m.id === message.id)) {
      this.log('⚠️ Skipping duplicate message:', message.id);
      return;
    }

    this.messages.push(message);
    this.renderMessage(message, this.messages.length - 1);
    this.log('✅ Added new message to UI:', message.id, `Total: ${this.messages.length}`);

    // Update unread count if widget is closed
    if (!this.isOpen && message.author !== 'customer') {
      this.unreadCount++;
      this.updateUnreadCount();
    }

    // Scroll to bottom
    this.scrollToBottom();
  }

  private removeMessage(id: string): void {
    const idx = this.messages.findIndex(message => message.id === id);
    if (idx === -1) return;

    this.messages.splice(idx, 1);
    this.renderMessages();
  }

  /**
   * Get welcome message HTML if no messages exist
   */
  private getWelcomeMessageHtml(): string {
    if (this.messages.length > 0) {
      return '';
    }

    const welcomeText = this.config.welcomeMessage || 'Hello! How can I help you today?';
    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return `
      <div class="welcome-message">
        <div class="message agent">
          <div class="message-content">
            <div class="message-text">${this.escapeHtml(welcomeText)}</div>
            <div class="message-time">${currentTime}</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render all messages
   */
  private renderMessages(): void {
    // Don't render messages if offline form is shown
    if (this.isOfflineFormShown) {
      this.log('📋 Offline form shown, skipping renderMessages');
      return;
    }

    const messagesContainer = this.container?.querySelector('#quantumchat-messages') as HTMLElement;
    if (!messagesContainer) return;

    // Clear container and show welcome message if no messages
    if (this.messages.length === 0) {
      messagesContainer.innerHTML = this.getWelcomeMessageHtml();
    } else {
      messagesContainer.innerHTML = '';
      this.messages.forEach((message, index) => {
        this.renderMessage(message, index, false);
      });
    }

    this.scrollToBottom();
  }

  /**
   * Render a single message
   */
  private renderMessage(message: SocketMessage, index: number, scrollToBottom: boolean = true): void {
    const messagesContainer = this.container?.querySelector('#quantumchat-messages') as HTMLElement;
    if (!messagesContainer) return;

    // Check if message element already exists
    let messageElement = messagesContainer.children[index] as HTMLElement;

    if (!messageElement) {
      // Create new message element
      messageElement = document.createElement('div');
      messageElement.className = `message ${message.author}`;
      messagesContainer.appendChild(messageElement);
    } else {
      // Update existing message element
      messageElement.className = `message ${message.author}`;
    }

    // Transfer notice: render as a centered system separator, not a chat bubble.
    if (
      message.message_type === 'transfer' ||
      (message.metadata && message.metadata.transfer_notice)
    ) {
      const transferText = this.escapeHtml(message.text || '');
      messageElement.className = 'transfer-notice';
      messageElement.setAttribute('role', 'status');
      messageElement.innerHTML = `
        <span class="transfer-notice-icon" aria-hidden="true">↗</span>
        <span class="transfer-notice-text">${transferText}</span>
      `;
      if (scrollToBottom) {
        this.scrollToBottom();
      }
      return;
    }

    // Format timestamp with intelligent date/time display
    const timestamp = message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp);
    const now = new Date();
    const isToday = timestamp.toDateString() === now.toDateString();
    const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === timestamp.toDateString();
    const isThisWeek = (now.getTime() - timestamp.getTime()) < 7 * 24 * 60 * 60 * 1000;

    let timeString: string;

    if (isToday) {
      // Today - show only time
      timeString = timestamp.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else if (isYesterday) {
      // Yesterday - show "Yesterday HH:MM"
      const timeOnly = timestamp.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      timeString = `Yesterday ${timeOnly}`;
    } else if (isThisWeek) {
      // This week - show "DDD HH:MM"
      const dayName = timestamp.toLocaleDateString([], { weekday: 'short' });
      const timeOnly = timestamp.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      timeString = `${dayName} ${timeOnly}`;
    } else {
      // Older - show date and time
      timeString = timestamp.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Create attachments HTML
    let attachmentsHtml = '';
    if (message.attachments && message.attachments.length > 0) {
      attachmentsHtml = message.attachments.map(attachment => {
        // Skip attachment if no URL
        if (!attachment.url) {
          this.log('⚠️ Attachment missing URL:', attachment);
          return '';
        }

        const isImage = attachment.type?.startsWith('image/') || attachment.type === 'image';
        const fullUrl = attachment.url.startsWith('http') ? attachment.url : `${this.config.apiUrl}${attachment.url}`;

        if (isImage) {
          return `<div class="message-attachment image-attachment">
            <img src="${fullUrl}" alt="${attachment.name || 'Image'}"
                 style="max-width: 200px; max-height: 150px; border-radius: 8px; margin: 8px 0; cursor: pointer;"
                 onclick="window.open('${fullUrl}', '_blank')" />
          </div>`;
        } else {
          const sizeText = attachment.size ? `(${(attachment.size / 1024).toFixed(1)} KB)` : '';
          return `<div class="message-attachment file-attachment">
            <div style="display: flex; align-items: center; background: #f5f5f5; border-radius: 8px; padding: 12px; margin: 8px 0; border: 1px solid #e0e0e0;">
              <div style="font-size: 20px; margin-right: 12px;">📎</div>
              <div style="flex: 1; min-width: 0;">
                <div style="font-weight: 500; color: #333; font-size: 14px;">${attachment.name || 'File'}</div>
                <div style="color: #666; font-size: 12px;">${sizeText}</div>
              </div>
              <button onclick="window.open('${fullUrl}', '_blank')"
                      style="background: #007bff; color: white; border: none; border-radius: 4px; padding: 6px 12px; cursor: pointer; font-size: 12px;">
                📄 View
              </button>
            </div>
          </div>`;
        }
      }).join('');
    }

    // Determine which text to display
    // For agent messages: show translation if available (in customer's language)
    // For customer messages: show original text
    let displayText: string;
    let showTranslationLoader = false;

    if (message.author === 'agent') {
      if (message.translation?.translatedText) {
        // Has translation - show it
        displayText = message.translation.translatedText;
      } else if (message.translationPending || (this.config.translationEnabled && !message.text)) {
        // Waiting for translation - show loader
        displayText = '';
        showTranslationLoader = true;
      } else {
        // No translation needed or not enabled - show original
        displayText = message.text;
      }
    } else {
      // Customer message - show original
      displayText = message.text;
    }

    // Email channel indicator
    let channelBadge = '';
    if (message.delivery_channel === 'email') {
      channelBadge = `<div class="message-channel-badge email">✉️ <span>via Email</span></div>`;
    }

    // Email delivery status (optional - for agent messages only)
    let deliveryStatus = '';
    if (message.delivery_channel === 'email' && message.author === 'agent') {
      if (!message.email_sent_at) {
        deliveryStatus = '<span class="email-status sending">⏳</span> ';
      } else if (message.email_delivered) {
        deliveryStatus = '<span class="email-status delivered">✅</span> ';
      } else if (message.email_sent_at && !message.email_delivered) {
        deliveryStatus = '<span class="email-status failed">❌</span> ';
      }
    }

    // Render quick replies if present
    let quickRepliesHtml = '';
    if (message.message_type === 'quick_replies' && message.metadata?.options) {
      quickRepliesHtml = `
        <div class="quick-replies">
          ${message.metadata.options.map(opt => `
            <button class="quick-reply-btn" data-value="${this.escapeHtml(opt.value)}" data-id="${this.escapeHtml(opt.id)}">
              ${this.escapeHtml(opt.label)}
            </button>
          `).join('')}
        </div>
      `;
    }

    // Render carousel if present
    let carouselHtml = '';
    if (message.message_type === 'carousel' && message.metadata?.cards) {
      carouselHtml = `
        <div class="carousel-container">
          <div class="carousel-track">
            ${message.metadata.cards.map(card => `
              <div class="carousel-card" data-card-id="${this.escapeHtml(card.id)}">
                ${card.image_url ? `<img src="${card.image_url}" alt="${this.escapeHtml(card.title)}" class="carousel-image">` : ''}
                <div class="carousel-content">
                  <h4 class="carousel-title">${this.escapeHtml(card.title)}</h4>
                  ${card.description ? `<p class="carousel-description">${this.escapeHtml(card.description)}</p>` : ''}
                  ${card.buttons?.map(btn => `
                    <button class="carousel-btn" data-type="${btn.type}" data-value="${this.escapeHtml(btn.value)}" data-id="${this.escapeHtml(btn.id)}">
                      ${this.escapeHtml(btn.label)}
                    </button>
                  `).join('') || ''}
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Translation loader HTML
    const translationLoaderHtml = showTranslationLoader
      ? `<div class="message-text translation-pending">
           <span class="translation-loader"></span>
         </div>`
      : '';

    // Render avatar for agent messages when:
    // 1. Agent is assigned to conversation
    // 2. This is the first message in a group from the same author
    let avatarHtml = '';
    if (message.author === 'agent' && this.assignedAgent) {
      const prevMessage = index > 0 ? this.messages[index - 1] : null;
      const isFirstInGroup = !prevMessage || prevMessage.author !== 'agent';
      if (isFirstInGroup) {
        avatarHtml = this.renderMessageAvatar(message);
      }
    }

    // Add class for grouped messages (messages from same author without avatar)
    const isGroupedMessage = message.author === 'agent' && this.assignedAgent && !avatarHtml;
    if (isGroupedMessage) {
      messageElement.classList.add('grouped');
    } else {
      messageElement.classList.remove('grouped');
    }

    messageElement.innerHTML = `
      ${avatarHtml}
      <div class="message-content">
        ${channelBadge}
        ${attachmentsHtml}
        ${showTranslationLoader ? translationLoaderHtml : (displayText ? `<div class="message-text">${this.linkify(displayText)}</div>` : '')}
        ${quickRepliesHtml}
        ${carouselHtml}
        <div class="message-time">${deliveryStatus}${timeString}</div>
      </div>
    `;

    // Track read receipts for agent messages
    if (message.author === 'agent' && message.msg_id && this.readReceiptTracker) {
      this.readReceiptTracker.observe(messageElement, message.msg_id, 'agent');
    }

    if (scrollToBottom) {
      this.scrollToBottom();
    }
  }

  /**
   * Render avatar HTML for a message
   */
  private renderMessageAvatar(message: SocketMessage): string {
    const initials = message.author_initials || { letters: '?', color: '#9ca3af' };
    const avatarUrl = message.author_avatar_url;

    if (avatarUrl) {
      // Full URL for external avatars (Gravatar), relative for uploaded
      const fullUrl = avatarUrl.startsWith('http') ? avatarUrl : `${this.config.apiUrl}${avatarUrl}`;
      return `
        <div class="message-avatar">
          <img
            src="${fullUrl}"
            alt="${message.author_name || 'Agent'}"
            class="avatar-image"
            onerror="this.style.display='none';this.nextElementSibling.style.display='flex';"
          />
          <span
            class="avatar-initials"
            style="display:none;background-color:${initials.color}"
          >${initials.letters}</span>
        </div>
      `;
    }

    // No avatar URL - show initials
    return `
      <div class="message-avatar">
        <span
          class="avatar-initials"
          style="background-color:${initials.color}"
        >${initials.letters}</span>
      </div>
    `;
  }

  /**
   * Create the widget container and UI
   */
  private createContainer(): void {
    // Remove existing widget if present
    const existing = document.getElementById('quantumchat-widget-v2');
    if (existing) {
      existing.remove();
    }

    // Create main container
    this.container = document.createElement('div');
    this.container.id = 'quantumchat-widget-v2';
    this.container.className = `quantumchat-widget ${this.config.theme} ${this.config.position}`;
    applyTextDirection(this.container, this.currentLocale);

    // Set primary color CSS variable
    this.container.style.setProperty('--primary-color', this.config.primaryColor!);

    // Add widget HTML structure from original widget
    this.container.innerHTML = `
      <div class="quantumchat-launcher ${this.isOpen ? 'hidden' : ''}" id="quantumchat-launcher">
        <div class="launcher-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H18L22 22V4C22 2.9 21.1 2 20 2M6 9H18V11H6V9M6 12H16V14H6V12M6 6H18V8H6V6Z"/>
          </svg>
          ${this.unreadCount > 0 ? `<div class="unread-badge">${this.unreadCount}</div>` : ''}
        </div>
      </div>

      <div class="quantumchat-window ${this.isOpen ? 'open' : ''}" id="quantumchat-window">
        <div class="chat-header">
          <div class="header-content">
            <div class="agent-info">
              <div class="agent-avatars"></div>
              <div class="agent-details">
                <div class="agent-name">${this.escapeHtml(this.config.headerTitle || 'Customer Support')}</div>
                <div class="agent-status">Online</div>
              </div>
            </div>
            <button class="close-button" id="quantumchat-close">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="chat-messages" id="quantumchat-messages">
          ${this.getWelcomeMessageHtml()}
        </div>

        <div class="chat-input-container">
          <div id="file-upload-area" class="file-upload-area" style="display: none;">
            <div class="file-info">
              <span id="file-name">No file selected</span>
            </div>
            <button class="remove-file" id="remove-file">×</button>
          </div>

          <div id="emoji-picker-container" style="display: none; position: absolute; bottom: 60px; inset-inline-start: 50px; inset-inline-end: 16px; max-width: 320px; z-index: 1000; background: white; border-radius: 8px; box-shadow: 0 -4px 12px rgba(0,0,0,0.15);"></div>

          <div class="chat-input-wrapper">
            <input type="file" id="file-input" accept="image/*,.pdf,.doc,.docx" style="display: none;">
            <div class="input-buttons">
              <button class="attachment-button" id="attachment-button" title="Add attachment">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5S8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5S13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5S11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18S15 16.88 15 15.5V5C15 2.79 13.21 1 11 1S7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23S18 20.54 18 17.5V6H16.5Z"/>
                </svg>
              </button>
              <button class="emoji-button" id="emoji-button" title="Add emoji">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M12,17.5C14.33,17.5 16.3,16.04 17.11,14H6.89C7.69,16.04 9.67,17.5 12,17.5M8.5,11A1.5,1.5 0 0,0 10,9.5A1.5,1.5 0 0,0 8.5,8A1.5,1.5 0 0,0 7,9.5A1.5,1.5 0 0,0 8.5,11M15.5,11A1.5,1.5 0 0,0 17,9.5A1.5,1.5 0 0,0 15.5,8A1.5,1.5 0 0,0 14,9.5A1.5,1.5 0 0,0 15.5,11M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
              </button>
            </div>
            <textarea
              id="quantumchat-input"
              placeholder="${this.escapeHtml(this.config.inputPlaceholder || 'Type your message...')}"
              maxlength="1000"
              rows="1"
              style="resize: none; min-height: 40px; max-height: 120px; overflow-y: auto;"
            ></textarea>
            <button class="send-button" id="quantumchat-send" disabled title="Send message">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="powered-by">
          Powered by <a href="https://chataptor.com" target="_blank" rel="noopener noreferrer"><strong>chataptor.com</strong></a>
        </div>
      </div>
    `;

    // Add to body and setup event listeners
    const targetContainer = this.config.containerId
      ? document.getElementById(this.config.containerId)
      : document.body;

    if (targetContainer) {
      targetContainer.appendChild(this.container);
      this.setupWidgetEventListeners();
      // Initial header avatars render (may be empty placeholder)
      this.renderHeaderAvatars();
    }
  }

  // Update internal online agents state (limit 5) and re-render header avatars
  private updateOnlineAgents(agents: Array<{ id: number; name: string; avatar_url?: string | null; initials?: { letters: string; color: string } }>) {
    this.onlineAgents = (agents || []).slice(0, 5);
    this.renderHeaderAvatars();
  }

  // Render stacked avatars in the header
  private renderHeaderAvatars() {
    const container = this.container?.querySelector('.agent-avatars') as HTMLElement | null;
    if (!container) return;

    // If agent is assigned, show only their avatar (no stack)
    if (this.assignedAgent) {
      const agent = {
        id: this.assignedAgent.agent_id,
        name: this.assignedAgent.name,
        avatar_url: this.assignedAgent.avatar_url,
        initials: this.assignedAgent.initials
      };
      container.innerHTML = this.renderHeaderAvatarItem(agent);
      return;
    }

    // Otherwise show stack of online agents
    if (!this.onlineAgents || this.onlineAgents.length === 0) {
      container.innerHTML = `<div class="agent-avatar placeholder"></div>`;
      return;
    }

    const avatarsHtml = this.onlineAgents.map(a => this.renderHeaderAvatarItem(a)).join('');
    container.innerHTML = `<div class="avatar-stack">${avatarsHtml}</div>`;
  }

  // Update header when agent is assigned to conversation
  private updateHeaderWithAssignedAgent() {
    if (!this.assignedAgent) return;

    // Update avatar
    this.renderHeaderAvatars();

    // Update name in header
    const agentNameEl = this.container?.querySelector('.agent-name') as HTMLElement | null;
    if (agentNameEl) {
      agentNameEl.textContent = this.assignedAgent.name;
    }
  }

  // Extract assigned agent data from conversation and update header
  private setAssignedAgentFromConversation(data: ConversationData) {
    if (data.assigned_agent_id && data.assigned_agent_name) {
      this.assignedAgent = {
        agent_id: data.assigned_agent_id,
        name: data.assigned_agent_name,
        avatar_url: data.assigned_agent_avatar_url,
        initials: data.assigned_agent_initials || undefined
      };
      this.log('👤 Set assigned agent from conversation data:', this.assignedAgent);
      this.updateHeaderWithAssignedAgent();
    }
  }

  private renderHeaderAvatarItem(agent: { id: number; name: string; avatar_url?: string | null; initials?: { letters: string; color: string } }): string {
    const initials = agent.initials || { letters: '?', color: '#9ca3af' };
    const avatarUrl = agent.avatar_url;
    if (avatarUrl) {
      const fullUrl = avatarUrl.startsWith('http') ? avatarUrl : `${this.config.apiUrl}${avatarUrl}`;
      return `
        <div class="avatar">
          <img
            src="${fullUrl}"
            alt="${this.escapeHtml(agent.name || 'Agent')}"
            class="avatar-image"
            onerror="this.style.display='none';this.nextElementSibling.style.display='flex';"
          />
          <span class="avatar-initials" style="display:none;background-color:${initials.color}">${this.escapeHtml(initials.letters)}</span>
        </div>
      `;
    }
    return `
      <div class="avatar">
        <span class="avatar-initials" style="background-color:${initials.color}">${this.escapeHtml(initials.letters)}</span>
      </div>
    `;
  }

  /**
   * Setup global page event listeners
   */
  private setupEventListeners(): void {
    // Handle page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOpen) {
        this.markMessagesAsRead();
      }
    });

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.socketManager.disconnect();
    });

    // Setup external triggers (data-attributes on page elements)
    this.setupExternalTriggers();

    // Re-scan for triggers when DOM changes (for SPAs)
    this.observeDOMChanges();

    // Setup chatbot interactive elements (quick replies, carousel)
    this.setupChatbotListeners();
  }

  /**
   * Setup event listeners for chatbot interactive elements (quick replies, carousel buttons)
   */
  private setupChatbotListeners(): void {
    // Use event delegation on the messages container
    const setupListeners = () => {
      const messagesContainer = this.container?.querySelector('#quantumchat-messages');
      if (!messagesContainer) {
        // Retry after a short delay if container not ready
        setTimeout(setupListeners, 100);
        return;
      }

      messagesContainer.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        // Handle quick reply button clicks
        const quickReplyBtn = target.closest('.quick-reply-btn') as HTMLElement;
        if (quickReplyBtn) {
          e.preventDefault();
          const value = quickReplyBtn.getAttribute('data-value');
          if (value) {
            this.log('🔘 Quick reply selected:', value);
            this.sendMessage(value);
            // Hide all quick replies after selection
            this.hideQuickReplies();
          }
          return;
        }

        // Handle carousel button clicks
        const carouselBtn = target.closest('.carousel-btn') as HTMLElement;
        if (carouselBtn) {
          e.preventDefault();
          const btnType = carouselBtn.getAttribute('data-type');
          const value = carouselBtn.getAttribute('data-value');

          if (btnType === 'url' && value) {
            // Open URL in new tab
            window.open(value, '_blank');
          } else if (btnType === 'select' && value) {
            this.log('🎠 Carousel button selected:', value);
            this.sendMessage(value);
          }
          return;
        }
      });
    };

    setupListeners();
  }

  /**
   * Hide all quick reply buttons (after user selection)
   */
  private hideQuickReplies(): void {
    const quickReplies = this.container?.querySelectorAll('.quick-replies');
    quickReplies?.forEach(qr => {
      (qr as HTMLElement).style.display = 'none';
    });
  }

  /**
   * Setup external triggers based on data-attributes
   *
   * Supported attributes:
   * - data-quantumchat-open: Opens the chat widget on click
   * - data-quantumchat-toggle: Toggles the chat widget on click
   * - data-quantumchat-close: Closes the chat widget on click
   * - data-quantumchat-message="text": Prefills message when opening
   *
   * Examples:
   * <a href="#" data-quantumchat-open>Contact us</a>
   * <button data-quantumchat-open data-quantumchat-message="I need help with...">Help</button>
   * <span data-quantumchat-toggle>Chat</span>
   */
  private setupExternalTriggers(): void {
    // Find all elements with quantumchat trigger attributes
    const openTriggers = document.querySelectorAll('[data-quantumchat-open]');
    const toggleTriggers = document.querySelectorAll('[data-quantumchat-toggle]');
    const closeTriggers = document.querySelectorAll('[data-quantumchat-close]');

    this.log(`🎯 Found external triggers: ${openTriggers.length} open, ${toggleTriggers.length} toggle, ${closeTriggers.length} close`);

    // Setup open triggers
    openTriggers.forEach((element) => {
      if (element.hasAttribute('data-quantumchat-bound')) return; // Already bound

      element.addEventListener('click', (e) => {
        e.preventDefault();
        const message = element.getAttribute('data-quantumchat-message') || undefined;
        this.open(message);
      });

      element.setAttribute('data-quantumchat-bound', 'true');
      this.log('🔗 Bound open trigger to element:', element);
    });

    // Setup toggle triggers
    toggleTriggers.forEach((element) => {
      if (element.hasAttribute('data-quantumchat-bound')) return;

      element.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.isOpen) {
          const message = element.getAttribute('data-quantumchat-message') || undefined;
          this.open(message);
        } else {
          this.close();
        }
      });

      element.setAttribute('data-quantumchat-bound', 'true');
      this.log('🔗 Bound toggle trigger to element:', element);
    });

    // Setup close triggers
    closeTriggers.forEach((element) => {
      if (element.hasAttribute('data-quantumchat-bound')) return;

      element.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });

      element.setAttribute('data-quantumchat-bound', 'true');
      this.log('🔗 Bound close trigger to element:', element);
    });
  }

  /**
   * Observe DOM changes to handle dynamically added elements (SPAs)
   */
  private observeDOMChanges(): void {
    const observer = new MutationObserver((mutations) => {
      let shouldRescan = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes have quantumchat attributes
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              if (node.hasAttribute('data-quantumchat-open') ||
                  node.hasAttribute('data-quantumchat-toggle') ||
                  node.hasAttribute('data-quantumchat-close') ||
                  node.querySelector('[data-quantumchat-open], [data-quantumchat-toggle], [data-quantumchat-close]')) {
                shouldRescan = true;
              }
            }
          });
        }
      }

      if (shouldRescan) {
        this.log('🔄 DOM changed, rescanning for external triggers...');
        this.setupExternalTriggers();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Manually refresh external triggers (useful for SPAs)
   */
  public refreshTriggers(): void {
    this.log('🔄 Manually refreshing external triggers...');
    this.setupExternalTriggers();
  }

  /**
   * Setup widget event listeners
   */
  private setupWidgetEventListeners(): void {
    // Launcher button click
    const launcher = this.container?.querySelector('#quantumchat-launcher');
    launcher?.addEventListener('click', () => this.toggle());

    // Launcher hover-open. Initial mode from data-open-on; site_config may switch it later.
    this.hoverOpenEnabled = this.config.openOn === 'hover';
    this.setupHoverOpen(launcher);

    // Close button click
    const closeButton = this.container?.querySelector('#quantumchat-close');
    closeButton?.addEventListener('click', () => this.toggle());

    // Send button click
    const sendButton = this.container?.querySelector('#quantumchat-send');
    sendButton?.addEventListener('click', () => {
      console.log('🔘 [sendButton] Send button clicked!');
      console.log('📊 [sendButton] Button state:', {
        disabled: (sendButton as HTMLButtonElement).disabled,
        conversationId: this.conversationId,
        socketState: this.socketManager.getConnectionState()
      });
      if ((this.pasteHandler?.getQueue().length ?? 0) > 0) {
        void this.sendMessageWithAttachments().catch((error) => {
          this.log('❌ sendMessageWithAttachments failed:', error);
          this.showErrorMessage('Failed to send. Please try again.');
        });
      } else {
        void this.sendMessage();
      }
    });

    // Input field events
    const input = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
    if (input) {
      input.addEventListener('input', () => {
        this.updateSendButton();
        this.autoResize(input);

        // Send typing preview if enabled
        if (this.typingPreviewEnabled) {
          const text = input.value;
          console.log('⌨️  [Widget] Input event, text:', text.substring(0, 30));

          // Clear existing debounce timeout
          if (this.previewDebounceTimeout) {
            clearTimeout(this.previewDebounceTimeout);
          }

          // Debounce the preview update (300ms)
          this.previewDebounceTimeout = setTimeout(() => {
            if (text !== this.lastSentPreviewText) {
              this.lastSentPreviewText = text;
              console.log('⌨️  [Widget] Sending typing preview:', text.substring(0, 30));
              this.socketManager.sendTypingPreview(text);
            }
          }, 300);
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if ((this.pasteHandler?.getQueue().length ?? 0) > 0) {
            void this.sendMessageWithAttachments().catch((error) => {
              this.log('❌ sendMessageWithAttachments failed:', error);
              this.showErrorMessage('Failed to send. Please try again.');
            });
          } else {
            void this.sendMessage();
          }
        }
      });
    }

    // Emoji picker
    const emojiButton = this.container?.querySelector('#emoji-button') as HTMLElement;
    const emojiPickerContainer = this.container?.querySelector('#emoji-picker-container') as HTMLElement;

    emojiButton?.addEventListener('click', (e) => {
      e.stopPropagation();

      // Check if picker is currently hidden
      const isHidden = emojiPickerContainer.style.display === 'none' ||
                       getComputedStyle(emojiPickerContainer).display === 'none';


      if (isHidden) {
        // Create emoji picker if not exists
        if (!emojiPickerContainer.querySelector('emoji-picker')) {
          const picker = document.createElement('emoji-picker');
          picker.setAttribute('locale', this.currentLocale);
          picker.addEventListener('emoji-click', (event: any) => {
            const emoji = event.detail.unicode || event.detail.emoji?.unicode;
            if (emoji && input) {
              const start = input.selectionStart;
              const end = input.selectionEnd;
              const text = input.value;
              input.value = text.substring(0, start) + emoji + text.substring(end);
              input.selectionStart = input.selectionEnd = start + emoji.length;
              input.focus();
              input.dispatchEvent(new Event('input'));
            }
            emojiPickerContainer.style.display = 'none';
          });
          emojiPickerContainer.appendChild(picker);
        }
        emojiPickerContainer.style.display = 'block';
        console.log('✅ Emoji picker shown'); // Debug log
      } else {
        emojiPickerContainer.style.display = 'none';
        console.log('❌ Emoji picker hidden'); // Debug log
      }
    });

    // Close emoji picker when clicking outside
    document.addEventListener('click', (e) => {
      if (!emojiButton?.contains(e.target as Node) && !emojiPickerContainer?.contains(e.target as Node)) {
        if (emojiPickerContainer) {
          emojiPickerContainer.style.display = 'none';
        }
      }
    });

    // Attachment functionality
    const attachmentButton = this.container?.querySelector('#attachment-button') as HTMLElement;
    const fileInput = this.container?.querySelector('#file-input') as HTMLInputElement;

    attachmentButton?.addEventListener('click', () => {
      fileInput?.click();
    });

    fileInput?.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.handleFileUpload(target.files[0]);
      }
    });

    // Remove file button
    const removeFileButton = this.container?.querySelector('#remove-file') as HTMLElement;
    removeFileButton?.addEventListener('click', () => {
      this.removeFile();
    });

    this.setupPasteHandler();
  }

  private setupPasteHandler(): void {
    const container = this.container;
    const textArea = container?.querySelector('#quantumchat-input') as HTMLTextAreaElement | null;

    if (!container || !textArea) {
      this.log('⚠️ Cannot attach FilePasteHandler — container/textarea not ready');
      return;
    }

    this.pasteHandler?.destroy();
    this.pasteHandler = new FilePasteHandler(
      container,
      textArea,
      (pending) => {
        this.renderPendingAttachment(pending);
        this.updateSendButton();
      },
      () => this.showToast(this.translations.attachment_too_many ?? 'You can add up to 3 files at once'),
      (file, reason) => this.showValidationError(file, reason),
      this.uploadFileOnly.bind(this)
    );
    this.pasteHandler.attach();
  }

  /**
   * Update send button state
   */
  private updateSendButton(): void {
    const input = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
    const sendButton = this.container?.querySelector('#quantumchat-send') as HTMLButtonElement;

    if (input && sendButton) {
      const hasText = input.value.trim().length > 0;
      const hasQueuedAttachments = (this.pasteHandler?.getQueue().length ?? 0) > 0;
      sendButton.disabled = !hasText && !hasQueuedAttachments;
    }
  }

  /**
   * Auto-resize textarea
   */
  private autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = '40px';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }

  /**
   * Update unread count display
   */
  private updateUnreadCount(): void {
    const launcher = this.container?.querySelector('#quantumchat-launcher');
    const existingBadge = launcher?.querySelector('.unread-badge');

    if (this.unreadCount > 0) {
      if (existingBadge) {
        existingBadge.textContent = String(this.unreadCount);
      } else {
        const launcherIcon = launcher?.querySelector('.launcher-icon');
        if (launcherIcon) {
          const badge = document.createElement('div');
          badge.className = 'unread-badge';
          badge.textContent = String(this.unreadCount);
          launcherIcon.appendChild(badge);
        }
      }
    } else if (existingBadge) {
      existingBadge.remove();
    }
  }

  /**
   * Update connection indicator
   */
  private updateConnectionIndicator(connected: boolean): void {
    const status = this.container?.querySelector('.agent-status');
    if (status) {
      status.textContent = connected ? 'Online' : 'Connecting...';
    }
  }

  /**
   * Scroll messages to bottom
   */
  private scrollToBottom(): void {
    const messagesContainer = this.container?.querySelector('#quantumchat-messages') as HTMLElement;
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  /**
   * Mark messages as read
   * Uses ReadReceiptTracker to mark all visible agent messages as read
   */
  private markMessagesAsRead(): void {
    if (this.readReceiptTracker) {
      this.log('📖 Marking visible messages as read');
      this.readReceiptTracker.markVisibleAsRead();
    }
  }

  /**
   * Show error message to user
   */
  private showErrorMessage(message: string): void {
    // Could show a toast or error in the chat
    this.log('❌ Error:', message);
  }

  private renderPendingAttachment(pending: PendingUpload): void {
    const inputContainer = this.container?.querySelector('.chat-input-container') as HTMLElement | null;
    if (!inputContainer) return;

    let previewList = inputContainer.querySelector(':scope > .quantumchat-attachments-preview') as HTMLElement | null;
    if (!previewList) {
      previewList = document.createElement('div');
      previewList.className = 'quantumchat-attachments-preview';
      inputContainer.insertBefore(previewList, inputContainer.firstChild);
    }

    const card = document.createElement('div');
    card.className = `quantumchat-attachment-card status-${pending.status}`;
    card.dataset.attachmentId = pending.id;

    if (pending.file.type.startsWith('image/') && pending.previewUrl) {
      const img = document.createElement('img');
      img.className = 'quantumchat-attachment-thumb';
      img.src = pending.previewUrl;
      img.alt = pending.file.name;
      card.appendChild(img);
    } else {
      const icon = document.createElement('div');
      icon.className = 'quantumchat-attachment-icon';
      icon.textContent = 'DOC';
      card.appendChild(icon);
    }

    const name = document.createElement('span');
    name.className = 'quantumchat-attachment-name';
    name.textContent = pending.file.name;
    card.appendChild(name);

    const size = document.createElement('span');
    size.className = 'quantumchat-attachment-size';
    size.textContent = this.formatFileSize(pending.file.size);
    card.appendChild(size);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'quantumchat-attachment-cancel';
    cancelBtn.textContent = 'x';
    cancelBtn.setAttribute('aria-label', 'Usun zalacznik');
    cancelBtn.addEventListener('click', () => {
      this.pasteHandler?.removeFromQueue(pending.id);
      card.remove();
      if (previewList && previewList.children.length === 0) {
        previewList.remove();
      }
      this.updateSendButton();
    });
    card.appendChild(cancelBtn);

    previewList.appendChild(card);
  }

  private refreshPendingAttachmentsPreview(): void {
    if (!this.pasteHandler) return;

    for (const item of this.pasteHandler.getQueue()) {
      const card = Array.from(
        this.container?.querySelectorAll('.quantumchat-attachment-card') ?? []
      ).find(el => (el as HTMLElement).dataset.attachmentId === item.id) as HTMLElement | undefined;
      if (!card) continue;

      card.className = `quantumchat-attachment-card status-${item.status}`;
      card.querySelector('.quantumchat-attachment-retry')?.remove();

      if (item.status === 'error') {
        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.className = 'quantumchat-attachment-retry';
        retryBtn.textContent = this.translations.attachment_retry ?? 'Retry';
        retryBtn.title = item.error ?? 'Upload failed';
        retryBtn.setAttribute(
          'aria-label',
          t(this.translations.attachment_retry_aria ?? 'Retry upload %{name}', { name: item.file.name })
        );
        retryBtn.addEventListener('click', async () => {
          retryBtn.disabled = true;
          await this.pasteHandler?.retry(item.id);
          this.refreshPendingAttachmentsPreview();
          this.updateSendButton();
        });
        card.appendChild(retryBtn);
      }
    }
  }

  private clearPendingAttachmentsPreview(): void {
    this.container?.querySelector('.quantumchat-attachments-preview')?.remove();
  }

  private showToast(message: string): void {
    this.container?.querySelector('.quantumchat-toast')?.remove();

    const toast = document.createElement('div');
    toast.className = 'quantumchat-toast';
    toast.textContent = message;
    this.container?.querySelector('.quantumchat-window')?.appendChild(toast);

    setTimeout(() => toast.remove(), 3000);
  }

  private showValidationError(file: File, reason: string): void {
    const tr = this.translations;
    const name = file.name;
    const messages: Record<string, string> = {
      too_large: t(tr.attachment_too_large ?? '%{name}: file is too large (max 10 MB)', { name }),
      too_many_files: tr.attachment_too_many ?? 'You can add up to 3 files at once',
      unsupported_mime: t(tr.attachment_unsupported ?? '%{name}: this file type is not supported', { name }),
      upload_failed: t(tr.attachment_upload_failed ?? '%{name}: failed to upload the file', { name }),
    };

    this.showToast(messages[reason] ?? t(tr.attachment_validation_error ?? '%{name}: validation error', { name }));
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  /**
   * Load or generate guest ID for anonymous user tracking
   */
  private loadOrGenerateGuestId(): void {
    try {
      // Try to load existing guest ID
      const stored = localStorage.getItem(this.guestStorageKey);
      if (stored) {
        this.guestId = stored;
        this.log('💾 Loaded guest ID from storage:', this.guestId);
      } else {
        // Generate new guest ID (UUID v4)
        this.guestId = this.generateUUID();
        localStorage.setItem(this.guestStorageKey, this.guestId);
        this.log('🆕 Generated new guest ID:', this.guestId);
      }
    } catch (error) {
      this.log('⚠️ Failed to load/generate guest ID:', error);
      // Fallback: generate ID without persisting
      this.guestId = this.generateUUID();
    }
  }

  /**
   * Generate UUID v4 for guest identification
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Load conversation from localStorage
   */
  private loadConversationFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.conversationId = data.conversationId;
        this.log('💾 Loaded conversation from storage:', this.conversationId);
      }
    } catch (error) {
      this.log('⚠️ Failed to load conversation from storage:', error);
    }
  }

  /**
   * Save conversation to localStorage
   */
  private saveConversationToStorage(): void {
    try {
      const data = {
        conversationId: this.conversationId,
        timestamp: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      this.log('💾 Saved conversation to storage:', this.conversationId);
    } catch (error) {
      this.log('⚠️ Failed to save conversation to storage:', error);
    }
  }

  /**
   * Clear conversation from localStorage
   */
  private clearConversationFromStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
      this.log('🗑️ Cleared conversation from storage');
    } catch (error) {
      this.log('⚠️ Failed to clear conversation from storage:', error);
    }
  }

  /**
   * Show system message in chat
   */
  private showSystemMessage(text: string): void {
    const systemMessage: SocketMessage = {
      conversationId: this.conversationId || '',
      text: text,
      author: 'bot',
      timestamp: new Date(),
      isTemporary: true
    };
    this.messages.push(systemMessage);
    this.renderMessages();
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Email validation state
  private emailValidationTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastValidatedEmail: string = '';

  /**
   * Validate email address via backend DNS/MX lookup
   * Returns validation result with status: valid, warning, invalid, error
   */
  private async validateEmailAsync(email: string): Promise<{valid: boolean; status: string; message: string | null}> {
    try {
      const response = await fetch(`${this.config.apiUrl}/api/widget/validate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Key': this.config.widgetKey,
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        return { valid: false, status: 'error', message: 'Validation failed' };
      }

      return await response.json();
    } catch (error) {
      this.log('Email validation error:', error);
      // On network error, accept the email (don't block user)
      return { valid: true, status: 'warning', message: 'Could not verify' };
    }
  }

  /**
   * Setup email validation on input with debounce
   */
  private setupEmailValidation(inputElement: HTMLInputElement): void {
    const statusContainer = document.createElement('div');
    statusContainer.className = 'quantumchat-email-status';
    statusContainer.style.display = 'none';
    inputElement.parentElement?.appendChild(statusContainer);

    const updateStatus = (status: 'verifying' | 'valid' | 'warning' | 'invalid' | 'hidden', message?: string) => {
      if (status === 'hidden') {
        statusContainer.style.display = 'none';
        inputElement.classList.remove('email-valid', 'email-invalid', 'email-warning');
        return;
      }

      statusContainer.style.display = 'flex';

      // Remove all status classes
      inputElement.classList.remove('email-valid', 'email-invalid', 'email-warning');
      statusContainer.classList.remove('status-verifying', 'status-valid', 'status-warning', 'status-invalid');

      switch (status) {
        case 'verifying':
          statusContainer.innerHTML = `<span class="spinner"></span> ${this.translations.email_verifying}`;
          statusContainer.classList.add('status-verifying');
          break;
        case 'valid':
          statusContainer.innerHTML = `<span class="check-icon">✓</span> ${this.translations.email_verified}`;
          statusContainer.classList.add('status-valid');
          inputElement.classList.add('email-valid');
          break;
        case 'warning':
          statusContainer.innerHTML = `<span class="warning-icon">⚠</span> ${message || this.translations.email_verified}`;
          statusContainer.classList.add('status-warning');
          inputElement.classList.add('email-warning');
          break;
        case 'invalid':
          statusContainer.innerHTML = `<span class="error-icon">✗</span> ${this.translations.email_domain_invalid}`;
          statusContainer.classList.add('status-invalid');
          inputElement.classList.add('email-invalid');
          break;
      }
    };

    const validateWithDebounce = () => {
      const email = inputElement.value.trim().toLowerCase();

      // Clear previous timeout
      if (this.emailValidationTimeout) {
        clearTimeout(this.emailValidationTimeout);
      }

      // Basic format check first
      if (!email || !email.includes('@') || email.length < 5) {
        updateStatus('hidden');
        return;
      }

      // Skip if already validated
      if (email === this.lastValidatedEmail) {
        return;
      }

      // Show verifying status
      updateStatus('verifying');

      // Debounce: wait 500ms before making API call
      this.emailValidationTimeout = setTimeout(async () => {
        const result = await this.validateEmailAsync(email);
        this.lastValidatedEmail = email;

        if (result.valid) {
          if (result.status === 'warning') {
            updateStatus('warning', result.message || undefined);
          } else {
            updateStatus('valid');
          }
        } else {
          updateStatus('invalid');
        }
      }, 500);
    };

    // Listen to input events
    inputElement.addEventListener('input', validateWithDebounce);
    inputElement.addEventListener('blur', validateWithDebounce);

    // If email is pre-filled, validate it
    if (inputElement.value) {
      validateWithDebounce();
    }
  }

  /**
   * Convert URLs in text to clickable links (opens in new window)
   * Escapes HTML first for security, then converts URLs to anchor tags
   */
  private linkify(text: string): string {
    if (!text) return '';

    // First escape HTML to prevent XSS
    const escaped = this.escapeHtml(text);

    // URL regex pattern - matches http(s):// and www. URLs
    const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

    return escaped.replace(urlPattern, (url) => {
      // Ensure URL has protocol for href
      const href = url.startsWith('www.') ? `https://${url}` : url;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="message-link">${url}</a>`;
    });
  }

  /**
   * Get widget state for debugging
   */
  public getState(): any {
    return {
      conversationId: this.conversationId,
      isOpen: this.isOpen,
      messageCount: this.messages.length,
      unreadCount: this.unreadCount,
      socketState: this.socketManager.getConnectionState(),
      config: this.config
    };
  }

  private async uploadFileOnly(file: File): Promise<UploadAttachmentResponse> {
    const fileType = this.getFileType(file);
    if (!fileType) {
      throw new Error('File type not supported');
    }

    const formData = new FormData();
    formData.append('file', file);

    if (this.conversationId) {
      formData.append('conversationId', this.conversationId.toString());
    }

    const response = await fetch(`${this.config.apiUrl}/api/v1/uploads/${fileType}`, {
      method: 'POST',
      headers: {
        'X-Widget-Key': this.config.widgetKey
      },
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: { message?: string };

      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private async sendMessageWithAttachments(): Promise<void> {
    if (this.isSendingAttachments) return;

    const textArea = this.container?.querySelector('#quantumchat-input') as HTMLTextAreaElement;
    const sendButton = this.container?.querySelector('#quantumchat-send') as HTMLButtonElement;
    if (!textArea || !sendButton || !this.pasteHandler) return;

    const text = textArea.value.trim();
    const queue = this.pasteHandler.getQueue();
    if (!text && queue.length === 0) return;

    const setSending = (sending: boolean) => {
      sendButton.disabled = sending;
      sendButton.classList.toggle('sending', sending);
      if (sending) {
        sendButton.setAttribute('aria-busy', 'true');
      } else {
        sendButton.removeAttribute('aria-busy');
      }
    };

    this.isSendingAttachments = true;
    setSending(true);

    try {
      const uploadResults = await this.pasteHandler.uploadAll();
      this.refreshPendingAttachmentsPreview();

      const successes = uploadResults.filter((result): result is UploadSuccess => result.success);
      const failures = uploadResults.filter((result): result is UploadFailure => !result.success);

      if (failures.length > 0) {
        const names = failures.map(failure => failure.file.name).join(', ');
        this.showToast(
          t(this.translations.attachment_send_failed ?? 'Failed to send: %{names}. Click Retry on the file.', { names })
        );
        return;
      }

      const attachmentIds = successes.map(result => result.attachmentId);
      const attachmentsMetadata: AttachmentInfo[] = successes.map(result => ({
        id: result.attachmentId,
        url: result.secureUrl,
        name: result.file.name,
        size: result.file.size,
        type: result.file.type.startsWith('image/') ? 'image' : result.file.type,
      }));

      const ok = await this.sendMessage(text, attachmentIds, attachmentsMetadata);
      if (!ok) return;

      this.pasteHandler.clearQueue();
      this.clearPendingAttachmentsPreview();
    } finally {
      this.isSendingAttachments = false;
      setSending(false);
      this.updateSendButton();
    }
  }

  /**
   * Handle file upload
   */
  private async handleFileUpload(file: File): Promise<void> {
    try {
      // Get upload limits from server
      const limits = await this.getUploadLimits();

      // Determine file type
      const fileType = this.getFileType(file);
      if (!fileType) {
        this.showErrorMessage('File type not supported');
        return;
      }

      // Check file size against server limits
      const maxSize = limits[fileType]?.maxSize || (10 * 1024 * 1024);
      if (file.size > maxSize) {
        this.showErrorMessage(`File too large. Maximum size: ${(maxSize / (1024 * 1024)).toFixed(1)}MB`);
        return;
      }

      // Check file extension
      const allowedTypes: string[] = limits[fileType]?.allowedTypes || [];
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!fileExt || !allowedTypes.some((ext: string) => ext.includes(fileExt))) {
        this.showErrorMessage(`File type not allowed. Allowed: ${allowedTypes.join(', ')}`);
        return;
      }

      // Show file upload area
      const fileUploadArea = this.container?.querySelector('#file-upload-area') as HTMLElement;
      const fileName = this.container?.querySelector('#file-name') as HTMLElement;

      if (fileUploadArea && fileName) {
        fileName.textContent = file.name;
        fileUploadArea.style.display = 'block';
      }

      // Upload file securely
      await this.uploadFileSecurely(file, fileType);

    } catch (error) {
      this.log('❌ File upload error:', error);
      this.showErrorMessage('File upload failed. Please try again.');
    }
  }

  /**
   * Determine file type
   */
  private getFileType(file: File): 'image' | 'document' | null {
    const imageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const documentTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    if (imageTypes.includes(file.type)) {
      return 'image';
    } else if (documentTypes.includes(file.type)) {
      return 'document';
    }
    return null;
  }

  /**
   * Get upload limits from server
   */
  private async getUploadLimits(): Promise<any> {
    try {
      this.log('🔍 Fetching upload limits with widget key:', this.config.widgetKey);

      const response = await fetch(`${this.config.apiUrl}/api/v1/uploads/limits`, {
        headers: {
          'X-Widget-Key': this.config.widgetKey
        }
      });

      this.log('📡 Upload limits response status:', response.status, response.statusText);

      if (response.ok) {
        const limits = await response.json();
        this.log('✅ Upload limits received:', limits);
        return limits;
      } else {
        const errorText = await response.text();
        this.log('❌ Upload limits request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Upload limits request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      this.log('⚠️ Could not fetch upload limits:', error);
      throw error;
    }
  }

  /**
   * Upload file securely
   */
  private async uploadFileSecurely(file: File, fileType: 'image' | 'document'): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (this.conversationId) {
        formData.append('conversationId', this.conversationId.toString());
      }

      this.log('📤 Starting file upload:', {
        fileName: file.name,
        fileType: fileType,
        fileSize: file.size,
        conversationId: this.conversationId,
        url: `${this.config.apiUrl}/api/v1/uploads/${fileType}`,
        widgetKey: this.config.widgetKey
      });

      const response = await fetch(`${this.config.apiUrl}/api/v1/uploads/${fileType}`, {
        method: 'POST',
        headers: {
          'X-Widget-Key': this.config.widgetKey
        },
        body: formData,
        credentials: 'include'
      });

      this.log('📡 Upload response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        this.log('❌ Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText}`);
      }

      const uploadResult = await response.json();
      this.log('✅ File uploaded successfully:', uploadResult);

      // Automatically send message with attachment
      await this.sendAttachmentMessage(uploadResult, fileType, file.name);

      // Hide file upload area after successful upload
      const fileUploadArea = this.container?.querySelector('#file-upload-area') as HTMLElement;
      if (fileUploadArea) {
        fileUploadArea.style.display = 'none';
      }

      return uploadResult;
    } catch (error) {
      const uploadError = error instanceof Error ? error : new Error(String(error));
      this.log('❌ Upload error details:', {
        fileName: file.name,
        fileType,
        error: uploadError.message,
        stack: uploadError.stack
      });
      throw error;
    }
  }

  /**
   * Send attachment message
   */
  private async sendAttachmentMessage(uploadResult: any, fileType: 'image' | 'document', fileName: string): Promise<void> {
    try {
      // Ensure conversation exists before sending attachment
      await this.ensureConversation();

      const attachmentIds = [uploadResult.attachmentId];

      this.log('📤 Sending attachment message with IDs:', attachmentIds);

      // Use the socket manager to send the message
      void this.socketManager.sendMessage('', attachmentIds).catch((error) => {
        this.log('❌ Failed to send legacy attachment message:', error);
      });

      // Show immediate feedback to user
      this.showAttachmentSentFeedback(uploadResult, fileType, fileName);
    } catch (error) {
      this.log('❌ Failed to send attachment:', error);
      this.showErrorMessage('Failed to send attachment. Please try again.');
    }
  }

  /**
   * Show attachment sent feedback
   */
  private showAttachmentSentFeedback(uploadResult: any, fileType: 'image' | 'document', fileName: string): void {
    const messagesContainer = this.container?.querySelector('#quantumchat-messages') as HTMLElement;
    if (!messagesContainer) return;

    // Add temporary "sending" message
    const tempMessage: SocketMessage = {
      id: `temp-attachment-${Date.now()}`,
      conversationId: this.conversationId || '',
      text: '',
      author: 'customer',
      timestamp: new Date(),
      isTemporary: true,
      attachments: [{
        id: uploadResult.attachmentId,
        url: uploadResult.secureUrl,
        size: uploadResult.processedSize || uploadResult.fileSize,
        type: fileType === 'image' ? 'image' : 'application/octet-stream',
        name: fileName
      }]
    };

    this.addMessage(tempMessage);
  }

  /**
   * Remove selected file
   */
  private removeFile(): void {
    const fileUploadArea = this.container?.querySelector('#file-upload-area') as HTMLElement;
    const fileInput = this.container?.querySelector('#file-input') as HTMLInputElement;

    if (fileUploadArea && fileInput) {
      fileUploadArea.style.display = 'none';
      fileInput.value = '';
    }
  }

  /**
   * Destroy the widget
   */
  public destroy(): void {
    this.cancelHoverOpen();
    if (this.hoverClickGuard) {
      document.removeEventListener('click', this.hoverClickGuard, true);
      this.hoverClickGuard = null;
    }
    this.pasteHandler?.destroy();
    this.pasteHandler = null;
    this.pageTracker?.stop();
    this.pageTracker = null;

    // Cleanup read receipt tracker
    if (this.readReceiptTracker) {
      this.readReceiptTracker.destroy();
      this.readReceiptTracker = null;
    }

    this.socketManager.disconnect();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    this.log('🗑️ Widget destroyed');
  }

  /**
   * Detect browser language for auto-translations
   * Returns ISO 639-1 code (e.g., 'ja', 'de', 'pl')
   */
  private detectBrowserLanguage(): string | undefined {
    if (typeof navigator === 'undefined') {
      return undefined;
    }

    // Get primary language from navigator
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (!browserLang) {
      return undefined;
    }

    // Extract language code (e.g., 'en-US' -> 'en', 'ja' -> 'ja')
    const langCode = browserLang.split('-')[0].toLowerCase();

    this.log('🌐 Detected browser language:', langCode, '(from:', browserLang, ')');

    return langCode;
  }

  /**
   * Apply auto-generated translations to widget
   * Called when translations:ready event is received
   */
  private applyAutoTranslations(variant: LanguageVariant): void {
    this.log('🌐 Applying auto-generated translations for:', variant.language_code);
    this.currentLocale = variant.language_code;
    if (this.container) applyTextDirection(this.container, this.currentLocale);

    // Update widget texts from variant
    if (variant.welcome_message) {
      this.config.welcomeMessage = variant.welcome_message;
    }
    if (variant.header_title) {
      this.config.headerTitle = variant.header_title;
    }
    if (variant.input_placeholder) {
      this.config.inputPlaceholder = variant.input_placeholder;
    }

    // Update translations object for offline form
    if (variant.offline_title) {
      this.translations.offline_title = variant.offline_title;
    }
    if (variant.offline_subtitle) {
      this.translations.offline_subtitle = variant.offline_subtitle;
    }
    if (variant.email_label) {
      this.translations.email_label = variant.email_label;
    }
    if (variant.email_placeholder) {
      this.translations.email_placeholder = variant.email_placeholder;
    }
    if (variant.name_label) {
      this.translations.name_label = variant.name_label;
    }
    if (variant.name_placeholder) {
      this.translations.name_placeholder = variant.name_placeholder;
    }
    if (variant.message_label) {
      this.translations.message_label = variant.message_label;
    }
    if (variant.message_placeholder) {
      this.translations.message_placeholder = variant.message_placeholder;
    }
    if (variant.submit_button) {
      this.translations.submit_button = variant.submit_button;
    }
    if (variant.success_title) {
      this.translations.success_title = variant.success_title;
    }
    if (variant.success_message) {
      this.translations.success_message = variant.success_message;
    }
    if (variant.bh_next_opening) this.translations.bh_next_opening = variant.bh_next_opening;
    if (variant.bh_next_opening_local) this.translations.bh_next_opening_local = variant.bh_next_opening_local;
    if (variant.bh_hours_label) this.translations.bh_hours_label = variant.bh_hours_label;
    if (variant.bh_closed) this.translations.bh_closed = variant.bh_closed;
    if (variant.bh_timezone_note) this.translations.bh_timezone_note = variant.bh_timezone_note;

    // Apply system translations
    if (variant.system_translations) {
      Object.entries(variant.system_translations).forEach(([key, value]) => {
        if (key in this.translations) {
          (this.translations as any)[key] = value;
        }
      });
    }

    // Update UI elements if widget is already rendered
    this.updateUIWithTranslations();

    this.log('✅ Auto-translations applied');
  }

  /**
   * Update rendered UI elements with current translations
   */
  private updateUIWithTranslations(): void {
    if (!this.container) return;

    // Update header title
    const headerTitle = this.container.querySelector('.header-title');
    if (headerTitle && this.config.headerTitle) {
      headerTitle.textContent = this.config.headerTitle;
    }

    // Update input placeholder
    const input = this.container.querySelector('#quantumchat-input') as HTMLInputElement;
    if (input && this.config.inputPlaceholder) {
      input.placeholder = this.config.inputPlaceholder;
    }

    // Update welcome message if visible
    const welcomeEl = this.container.querySelector('.welcome-message');
    if (welcomeEl && this.config.welcomeMessage) {
      welcomeEl.textContent = this.config.welcomeMessage;
    }
  }

  /**
   * Internal logging method
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.debug !== false) {
      console.log(`[QuantumchatWidgetV2] ${message}`, ...args);
    }
  }

  /**
   * Show offline form when no agents are available
   */
  private showOfflineForm(): void {
    if (this.isOfflineFormShown) return; // Prevent showing multiple times

    this.isOfflineFormShown = true;

    // Update header status to "Offline"
    const status = this.container?.querySelector('.agent-status');
    if (status) {
      status.textContent = 'Offline';
    }

    // Hide the standard input area to make room for the form
    const inputContainer = this.container?.querySelector('.chat-input-container') as HTMLElement;
    if (inputContainer) {
      inputContainer.style.display = 'none';
    }

    const messagesArea = this.container?.querySelector('#quantumchat-messages');
    if (!messagesArea) {
      console.error('❌ showOfflineForm: messages container not found');
      return;
    }
    console.log('✅ showOfflineForm: messages container found, rendering form');

    const tr = this.translations;
    messagesArea.innerHTML = `
      <div class="quantumchat-offline-form">
        <div class="quantumchat-offline-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#666" stroke-width="2"/>
            <path d="M12 8v4m0 4h.01" stroke="#666" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="quantumchat-offline-title">${tr.offline_title}</h3>
        <p class="quantumchat-offline-subtitle">${tr.offline_subtitle}</p>
        ${renderBusinessHours(
          this.businessHoursDisplay,
          this.currentLocale || 'en',
          (typeof Intl !== 'undefined' &&
            Intl.DateTimeFormat().resolvedOptions().timeZone) || '',
          new Date(),
          this.translations,
        )}
        <form id="quantumchat-offline-form">
          <div class="quantumchat-form-group">
            <label for="offline-email">${tr.email_label}</label>
            <input type="email" id="offline-email" name="email" placeholder="${tr.email_placeholder}" value="${this.config.customerEmail || ''}" required />
          </div>
          <div class="quantumchat-form-group">
            <label for="offline-name">${tr.name_label}</label>
            <input type="text" id="offline-name" name="name" placeholder="${tr.name_placeholder}" value="${this.config.customerName || ''}" />
          </div>
          <div class="quantumchat-form-group">
            <label for="offline-message">${tr.message_label}</label>
            <textarea id="offline-message" name="message" placeholder="${tr.message_placeholder}"></textarea>
          </div>
          <div style="position:absolute;left:-9999px;top:-9999px;opacity:0;height:0;width:0;overflow:hidden;" aria-hidden="true" tabindex="-1">
            <label for="offline-website">Website</label>
            <input type="text" id="offline-website" name="website" autocomplete="off" tabindex="-1" />
          </div>
          <button type="submit" class="quantumchat-offline-submit">${tr.submit_button}</button>
        </form>
        <div class="quantumchat-offline-success" style="display: none;">
          <div class="quantumchat-success-icon">✅</div>
          <p>${tr.success_message}</p>
        </div>
        <div class="quantumchat-offline-error" style="display: none;">
          <p>${tr.error_message}</p>
        </div>
      </div>
    `;

    // Setup form handler
    const form = this.container?.querySelector('#quantumchat-offline-form');
    form?.addEventListener('submit', (e) => this.handleOfflineFormSubmit(e));

    // Business hours expand/collapse
    const bh = this.container?.querySelector('.quantumchat-businesshours') as HTMLElement | null;
    const bhBtn = bh?.querySelector('.quantumchat-bh-summary') as HTMLButtonElement | null;
    if (bh && bhBtn) {
      bhBtn.addEventListener('click', () => {
        const expanded = bh.getAttribute('data-state') === 'expanded';
        bh.setAttribute('data-state', expanded ? 'collapsed' : 'expanded');
        bhBtn.setAttribute('aria-expanded', String(!expanded));
      });
    }

    // Setup real-time email validation (DNS/MX check)
    const emailInput = this.container?.querySelector('#offline-email') as HTMLInputElement;
    if (emailInput) {
      this.setupEmailValidation(emailInput);
    }
  }

  /**
   * Handle offline form submission
   * NEW: Creates conversation via WebSocket with channel_type="email"
   */
  private async handleOfflineFormSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const email = formData.get('email') as string;
    const name = formData.get('name') as string;
    const message = formData.get('message') as string;
    const website = formData.get('website') as string;

    // Client-side validation
    if (!email || !email.includes('@')) {
      alert(this.translations.email_validation_error);
      return;
    }

    try {
      this.log('📧 Creating email conversation from offline form');

      // Create conversation via WebSocket with channelType="email"
      // Pass detected language variant to set correct client_language
      const params: Record<string, string | undefined> = {
        customerEmail: email,
        channelType: 'email', // IMPORTANT: tells backend this is email-based conversation
        client_language: this.detectedVariant?.language_code,
      };

      // Only include name/message if non-empty to avoid sending "undefined" string
      if (name && name.trim()) {
        params.customerName = name.trim();
      }
      if (message && message.trim()) {
        params.message = message.trim();
      }
      // Honeypot field - should be empty for real users
      if (website) {
        params.website = website;
      }

      // Request conversation creation
      const success = await this.socketManager.createConversation(params);

      if (!success) {
        throw new Error('Failed to create conversation');
      }

      // Show success message
      form.style.display = 'none';
      const successDiv = this.container?.querySelector('.quantumchat-offline-success');
      if (successDiv) {
        // Update success message to show email
        const successMessage = successDiv.querySelector('p');
        if (successMessage) {
          successMessage.innerHTML = t(this.translations.success_message_with_email, {email: this.escapeHtml(email)});
        }
        (successDiv as HTMLElement).style.display = 'block';
      }

      this.log('✅ Email conversation created successfully');
      // Initial message is sent by backend during conversation creation

    } catch (error) {
      console.error('Failed to submit offline form:', error);

      const errorDiv = this.container?.querySelector('.quantumchat-offline-error');
      if (errorDiv) {
        (errorDiv as HTMLElement).style.display = 'block';
        const errorMessage = errorDiv.querySelector('p');
        if (errorMessage) {
          errorMessage.textContent = error instanceof Error ? error.message : this.translations.error_message;
        }
      }
    }
  }

  /**
   * Hide offline form (when agents come back online)
   */
  private hideOfflineForm(): void {
    this.isOfflineFormShown = false;

    // Restore header status to "Online"
    const status = this.container?.querySelector('.agent-status');
    if (status) {
      status.textContent = 'Online';
    }

    // Restore the standard input area
    const inputContainer = this.container?.querySelector('.chat-input-container') as HTMLElement;
    if (inputContainer) {
      inputContainer.style.display = '';
    }

    const offlineForm = this.container?.querySelector('.quantumchat-offline-form');
    if (offlineForm) {
      offlineForm.remove();
      this.renderMessages(); // Re-render messages
    }
  }

  // ==================== POPUP METHODS ====================

  /**
   * Fetch popups from server and setup triggers
   */
  private async fetchAndEvaluatePopups(): Promise<void> {
    try {
      this.log('🎯 Fetching popups for site...');

      const response = await fetch(`${this.config.apiUrl}/api/widget/popups?site_id=${encodeURIComponent(this.config.siteId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Key': this.config.widgetKey,
        },
      });

      if (!response.ok) {
        this.log('⚠️ Failed to fetch popups:', response.status);
        return;
      }

      const data = await response.json();
      this.popups = data.popups || [];

      this.log(`📋 Fetched ${this.popups.length} active popups`);

      // Setup triggers for each popup
      this.setupPopupTriggers();
    } catch (error) {
      this.log('❌ Error fetching popups:', error);
    }
  }

  /**
   * Setup triggers for all popups based on their display_flow
   */
  private setupPopupTriggers(): void {
    if (!this.visitorTracker || this.popups.length === 0) {
      return;
    }

    // Sort by priority (higher priority first)
    const sortedPopups = [...this.popups].sort((a, b) => (b.priority || 0) - (a.priority || 0));

    for (const popup of sortedPopups) {
      // Check if should be shown based on frequency settings
      if (!this.visitorTracker.shouldShowPopup(popup.id, popup.frequency)) {
        this.log(`⏭️ Popup ${popup.id} should not be shown (frequency check), skipping trigger setup`);
        continue;
      }

      // Find the trigger node in the flow
      const trigger = this.findTriggerNode(popup);

      if (!trigger) {
        // No trigger defined - evaluate immediately (backwards compatibility)
        this.log(`📋 Popup ${popup.id} has no trigger, evaluating immediately`);
        this.tryShowPopup(popup);
        continue;
      }

      this.setupTriggerForPopup(popup, trigger);
    }
  }

  /**
   * Find the trigger node in a popup's display flow
   */
  private findTriggerNode(popup: PopupConfig): FlowNode | null {
    const flow = popup.display_flow;
    if (!flow || !flow.nodes) {
      return null;
    }

    return flow.nodes.find(node => node.type.startsWith('trigger_')) || null;
  }

  /**
   * Setup a specific trigger for a popup
   */
  private setupTriggerForPopup(popup: PopupConfig, trigger: FlowNode): void {
    const triggerType = trigger.type;
    const triggerData = trigger.data || {};

    this.log(`🔧 Setting up trigger '${triggerType}' for popup ${popup.id}`, triggerData);

    switch (triggerType) {
      case 'trigger_page_load':
        // Immediate evaluation
        this.tryShowPopup(popup);
        break;

      case 'trigger_timer':
        this.setupTimerTrigger(popup, triggerData.seconds || 5);
        break;

      case 'trigger_scroll':
        this.setupScrollTrigger(popup, triggerData.percentage || 50);
        break;

      case 'trigger_exit_intent':
        this.setupExitIntentTrigger(popup);
        break;

      default:
        this.log(`⚠️ Unknown trigger type: ${triggerType}, evaluating immediately`);
        this.tryShowPopup(popup);
    }
  }

  /**
   * Setup timer trigger - show popup after X seconds
   */
  private setupTimerTrigger(popup: PopupConfig, seconds: number): void {
    this.log(`⏱️ Timer trigger: popup ${popup.id} will be evaluated in ${seconds}s`);

    const timerId = window.setTimeout(() => {
      this.triggerTimers.delete(popup.id);
      this.tryShowPopup(popup);
    }, seconds * 1000);

    this.triggerTimers.set(popup.id, timerId);
  }

  /**
   * Setup scroll trigger - show popup when user scrolls to X% of page
   */
  private setupScrollTrigger(popup: PopupConfig, percentage: number): void {
    this.log(`📜 Scroll trigger: popup ${popup.id} will be evaluated at ${percentage}% scroll`);

    if (this.scrollListenerActive) {
      // Already have a scroll listener, just add this popup to be checked
      return;
    }

    const scrollHandler = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;

      // Check all popups with scroll triggers
      for (const p of this.popups) {
        if (this.triggeredPopups.has(p.id)) continue;
        if (!this.visitorTracker?.shouldShowPopup(p.id, p.frequency)) continue;

        const trigger = this.findTriggerNode(p);
        if (trigger?.type === 'trigger_scroll') {
          const targetPercent = trigger.data?.percentage || 50;
          if (scrollPercent >= targetPercent) {
            this.log(`📜 Scroll threshold ${targetPercent}% reached for popup ${p.id}`);
            this.tryShowPopup(p);
          }
        }
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    this.scrollListenerActive = true;

    // Also check immediately in case page is already scrolled
    scrollHandler();
  }

  /**
   * Setup exit intent trigger - show popup when mouse leaves viewport (top)
   */
  private setupExitIntentTrigger(popup: PopupConfig): void {
    this.log(`🚪 Exit intent trigger: popup ${popup.id} will be shown on exit intent`);

    if (this.exitIntentListenerActive) {
      return;
    }

    const exitIntentHandler = (e: MouseEvent) => {
      // Only trigger when mouse leaves from the top of the viewport
      if (e.clientY > 0) return;

      // Check all popups with exit intent triggers
      for (const p of this.popups) {
        if (this.triggeredPopups.has(p.id)) continue;
        if (!this.visitorTracker?.shouldShowPopup(p.id, p.frequency)) continue;

        const trigger = this.findTriggerNode(p);
        if (trigger?.type === 'trigger_exit_intent') {
          this.log(`🚪 Exit intent detected for popup ${p.id}`);
          this.tryShowPopup(p);
          break; // Only show one popup
        }
      }
    };

    document.addEventListener('mouseout', exitIntentHandler);
    this.exitIntentListenerActive = true;
  }

  /**
   * Try to show a popup after trigger fires
   * Evaluates conditions and shows if all pass
   */
  private tryShowPopup(popup: PopupConfig): void {
    if (!this.visitorTracker) return;

    // Prevent double triggering
    if (this.triggeredPopups.has(popup.id)) {
      return;
    }

    // Check if should be shown based on frequency settings
    if (!this.visitorTracker.shouldShowPopup(popup.id, popup.frequency)) {
      this.log(`⏭️ Popup ${popup.id} should not be shown (frequency check)`);
      return;
    }

    // Check if another popup is active
    if (this.activePopup) {
      this.log(`⏭️ Another popup is active, queuing ${popup.id}`);
      return;
    }

    // Mark as triggered to prevent re-triggering
    this.triggeredPopups.add(popup.id);

    // Execute the flow starting from trigger
    this.executeFlow(popup);
  }

  /**
   * Execute the flow graph for a popup
   * Traverses nodes following edges and evaluating conditions
   */
  private async executeFlow(popup: PopupConfig): Promise<void> {
    const flow = popup.display_flow;

    // No flow or empty flow - show popup by default
    if (!flow || !flow.nodes || flow.nodes.length === 0) {
      this.log(`✅ Popup ${popup.id} has no flow, showing by default`);
      this.showPopup(popup);
      return;
    }

    // Find the trigger node (start point)
    const triggerNode = flow.nodes.find(n => n.type.startsWith('trigger_'));
    if (!triggerNode) {
      this.log(`✅ Popup ${popup.id} has no trigger, showing by default`);
      this.showPopup(popup);
      return;
    }

    // Start traversing from trigger
    await this.traverseFlowFrom(popup, flow, triggerNode.id, new Set());
  }

  /**
   * Traverse flow from a specific node
   */
  private async traverseFlowFrom(
    popup: PopupConfig,
    flow: DisplayFlow,
    nodeId: string,
    visited: Set<string>
  ): Promise<void> {
    // Prevent infinite loops
    if (visited.has(nodeId)) {
      this.log(`⚠️ Circular reference at node ${nodeId}, stopping`);
      return;
    }
    visited.add(nodeId);

    const node = flow.nodes.find(n => n.id === nodeId);
    if (!node) {
      this.log(`⚠️ Node ${nodeId} not found`);
      return;
    }

    this.log(`🔄 Processing node: ${node.type} (${nodeId})`);

    // Handle node based on type
    if (node.type.startsWith('trigger_')) {
      // Trigger nodes just pass through to next node
      const nextNodeId = this.getNextNodeId(flow, nodeId);
      if (nextNodeId) {
        await this.traverseFlowFrom(popup, flow, nextNodeId, visited);
      } else {
        // No next node after trigger - show popup
        this.showPopup(popup);
      }
    } else if (node.type.startsWith('condition_')) {
      // Evaluate condition and follow appropriate edge
      const result = this.evaluateConditionNode(node);
      this.log(`📊 Condition ${node.type} = ${result}`);

      const nextNodeId = this.getNextNodeIdForCondition(flow, nodeId, result);
      if (nextNodeId) {
        await this.traverseFlowFrom(popup, flow, nextNodeId, visited);
      } else {
        // No matching edge - if condition passed, show popup; otherwise stop
        if (result) {
          this.showPopup(popup);
        } else {
          this.log(`❌ Popup ${popup.id} condition failed, not showing`);
        }
      }
    } else if (node.type === 'action_show_popup') {
      this.log(`✅ Executing action_show_popup for ${popup.id}`);
      this.showPopup(popup);
    } else if (node.type === 'action_wait') {
      const seconds = node.data?.seconds || 2;
      this.log(`⏳ Waiting ${seconds} seconds...`);
      await this.wait(seconds * 1000);

      const nextNodeId = this.getNextNodeId(flow, nodeId);
      if (nextNodeId) {
        await this.traverseFlowFrom(popup, flow, nextNodeId, visited);
      }
    } else if (node.type === 'action_open_chat') {
      const message = node.data?.message || '';
      this.log(`💬 Opening chat widget${message ? ` with message: ${message}` : ''}`);
      this.openChatFromFlow(message);
    } else {
      // Unknown node type - try to continue
      const nextNodeId = this.getNextNodeId(flow, nodeId);
      if (nextNodeId) {
        await this.traverseFlowFrom(popup, flow, nextNodeId, visited);
      }
    }
  }

  /**
   * Get next node ID following default edge
   */
  private getNextNodeId(flow: DisplayFlow, fromNodeId: string): string | null {
    const edge = flow.edges.find(e => e.source === fromNodeId);
    return edge?.target || null;
  }

  /**
   * Get next node ID for condition based on result
   */
  private getNextNodeIdForCondition(flow: DisplayFlow, fromNodeId: string, result: boolean): string | null {
    const edges = flow.edges.filter(e => e.source === fromNodeId);

    // Look for edge with matching label
    const labelToFind = result ? 'yes' : 'no';
    const matchingEdge = edges.find(e => e.label === labelToFind || e.label === String(result));

    if (matchingEdge) {
      return matchingEdge.target;
    }

    // Fallback: if result is true and no labeled edge, use first available edge
    if (result && edges.length > 0) {
      return edges[0].target;
    }

    return null;
  }

  /**
   * Promise-based wait helper
   */
  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Open chat widget from flow action
   */
  private openChatFromFlow(prefillMessage: string = ''): void {
    this.openChat();

    // Pre-fill message if provided
    if (prefillMessage && this.chatContainer) {
      // Try to find input field and pre-fill it
      setTimeout(() => {
        const input = this.chatContainer?.querySelector('textarea, input[type="text"]') as HTMLInputElement | HTMLTextAreaElement;
        if (input) {
          input.value = prefillMessage;
          input.focus();
          // Trigger input event for reactive frameworks
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 500);
    }
  }

  /**
   * Evaluate a single condition node
   */
  private evaluateConditionNode(node: FlowNode): boolean {
    const data = node.data || {};

    switch (node.type) {
      case 'condition_url': {
        const operator = data.operator || 'contains';
        const pattern = data.pattern || '';
        const currentUrl = window.location.href;

        switch (operator) {
          case 'contains':
            return currentUrl.includes(pattern);
          case 'equals':
            return currentUrl === pattern;
          case 'starts_with':
            return currentUrl.startsWith(pattern);
          case 'ends_with':
            return currentUrl.endsWith(pattern);
          case 'matches_regex':
            try {
              return new RegExp(pattern).test(currentUrl);
            } catch {
              return false;
            }
          default:
            return true;
        }
      }

      case 'condition_visitor': {
        const visitorType = data.visitor_type || 'new';
        const actualType = this.visitorTracker?.getVisitorType();
        return actualType === visitorType;
      }

      case 'condition_device': {
        const expectedDevice = data.device || 'desktop';
        const actualDevice = this.getDeviceType();
        return actualDevice === expectedDevice;
      }

      case 'condition_page_views': {
        const operator = data.operator || 'greater_than';
        const count = data.count || 3;
        const pageViews = this.visitorTracker?.getPageViews() || 0;

        switch (operator) {
          case 'greater_than':
            return pageViews > count;
          case 'less_than':
            return pageViews < count;
          case 'equals':
            return pageViews === count;
          default:
            return true;
        }
      }

      case 'condition_time': {
        const startTime = data.start_time || '09:00';
        const endTime = data.end_time || '17:00';
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const [startH, startM] = startTime.split(':').map((n: string) => parseInt(n, 10));
        const [endH, endM] = endTime.split(':').map((n: string) => parseInt(n, 10));
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;

        // Handle overnight ranges (e.g., 22:00 - 06:00)
        if (startMinutes <= endMinutes) {
          return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
        } else {
          return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
        }
      }

      case 'condition_customer_type': {
        const operator = data.operator || 'equals';
        const expectedType = (data.customer_type || '').toLowerCase().trim();
        const actualType = (this.config.customerType || '').toLowerCase().trim();

        this.log(`🏷️ Customer type check: actual="${actualType}" ${operator} expected="${expectedType}"`);

        switch (operator) {
          case 'equals':
            return actualType === expectedType;
          case 'not_equals':
            return actualType !== expectedType;
          case 'contains':
            return actualType.includes(expectedType);
          default:
            return actualType === expectedType;
        }
      }

      default:
        this.log(`⚠️ Unknown condition type: ${node.type}`);
        return true;
    }
  }

  /**
   * Get device type (mobile, tablet, desktop)
   */
  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const ua = navigator.userAgent;
    if (/Mobile|Android|iP(hone|od)/.test(ua)) {
      return 'mobile';
    }
    if (/Tablet|iPad/.test(ua)) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Show a popup
   */
  private showPopup(popup: PopupConfig): void {
    if (this.activePopup) {
      this.log('⚠️ Another popup is already active, skipping');
      return;
    }

    this.activePopup = popup;

    const theme = popup.theme || {};

    // Create host element for Shadow DOM isolation
    this.popupContainer = document.createElement('div');
    this.popupContainer.id = `qc-popup-host-${popup.id}`;
    this.popupContainer.style.cssText = 'position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 999999;';

    // Attach Shadow DOM for complete CSS isolation
    const shadow = this.popupContainer.attachShadow({ mode: 'open' });

    // Add base styles inside Shadow DOM
    const styleEl = document.createElement('style');
    styleEl.textContent = this.getPopupBaseStyles();
    shadow.appendChild(styleEl);

    // Add custom CSS if provided
    if (popup.css_custom) {
      const customStyleEl = document.createElement('style');
      customStyleEl.textContent = popup.css_custom;
      shadow.appendChild(customStyleEl);
    }

    // Create popup structure based on type
    if (popup.type === 'modal') {
      const overlay = document.createElement('div');
      overlay.className = 'qc-popup-overlay';

      const popupBox = document.createElement('div');
      popupBox.className = 'qc-popup-box';
      popupBox.style.cssText = this.buildPopupBoxStyles(popup, theme);
      popupBox.innerHTML = this.buildPopupHTML(popup);

      overlay.appendChild(popupBox);
      shadow.appendChild(overlay);
    } else {
      const popupEl = document.createElement('div');
      popupEl.className = `qc-popup qc-popup-${popup.type}`;
      if (popup.position) {
        popupEl.classList.add(`qc-popup-${popup.position}`);
      }
      popupEl.style.cssText = this.buildPopupStyles(popup, theme);
      popupEl.innerHTML = this.buildPopupHTML(popup);

      shadow.appendChild(popupEl);
    }

    // Append to body
    document.body.appendChild(this.popupContainer);

    // Setup event listeners
    this.setupPopupEventListeners(popup);

    // Mark as shown
    if (this.visitorTracker) {
      this.visitorTracker.markPopupShown(popup.id);
    }

    // Track impression
    this.trackPopupEvent(popup.id, 'impression');

    this.log(`🎉 Popup ${popup.id} displayed`);
  }

  /**
   * Build popup inline styles from theme
   */
  private buildPopupStyles(popup: PopupConfig, theme: Record<string, any>): string {
    const styles: string[] = [];

    // Base styles
    styles.push('position: fixed');
    styles.push('z-index: 999999');
    styles.push('font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif');

    // Type-specific positioning
    switch (popup.type) {
      case 'modal':
        styles.push('top: 50%');
        styles.push('left: 50%');
        styles.push('transform: translate(-50%, -50%)');
        break;
      case 'bar':
        styles.push('left: 0');
        styles.push('right: 0');
        styles.push(popup.position === 'top' ? 'top: 0' : 'bottom: 0');
        break;
      case 'slide_in':
        styles.push(popup.position === 'left' ? 'left: 20px' : 'right: 20px');
        styles.push('bottom: 100px');
        break;
    }

    // Theme styles
    if (theme.background_color) {
      styles.push(`background-color: ${theme.background_color}`);
    }
    if (theme.text_color) {
      styles.push(`color: ${theme.text_color}`);
    }
    if (theme.border_radius && popup.type !== 'bar') {
      styles.push(`border-radius: ${theme.border_radius}`);
    }
    if (theme.width && popup.type !== 'bar') {
      styles.push(`width: ${theme.width}`);
      styles.push('max-width: 90vw');
    }

    // Box shadow
    styles.push('box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15)');

    // Background image (if set)
    const backgroundImage = (popup as any).background_image;
    if (backgroundImage) {
      styles.push(`background-image: url('${backgroundImage}')`);
      styles.push(`background-size: ${theme.background_size || 'cover'}`);
      styles.push(`background-position: ${theme.background_position || 'center'}`);
      styles.push('background-repeat: no-repeat');
    }

    return styles.join('; ');
  }

  /**
   * Build popup box styles for modal popups (the actual content box)
   */
  private buildPopupBoxStyles(popup: PopupConfig, theme: Record<string, any>): string {
    const styles: string[] = [];

    // Base styles for popup box
    styles.push('position: relative');
    styles.push('font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif');
    styles.push('max-width: 90vw');
    styles.push('max-height: 90vh');
    styles.push('overflow: auto');

    // Theme styles
    if (theme.background_color) {
      styles.push(`background-color: ${theme.background_color}`);
    } else {
      styles.push('background-color: #ffffff');
    }
    if (theme.text_color) {
      styles.push(`color: ${theme.text_color}`);
    }
    if (theme.border_radius) {
      styles.push(`border-radius: ${theme.border_radius}`);
    } else {
      styles.push('border-radius: 12px');
    }
    if (theme.width) {
      styles.push(`width: ${theme.width}`);
    } else {
      styles.push('width: 400px');
    }

    // Box shadow
    styles.push('box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15)');

    // Background image (if set)
    const backgroundImage = (popup as any).background_image;
    if (backgroundImage) {
      styles.push(`background-image: url('${backgroundImage}')`);
      styles.push(`background-size: ${theme.background_size || 'cover'}`);
      styles.push(`background-position: ${theme.background_position || 'center'}`);
      styles.push('background-repeat: no-repeat');
    }

    return styles.join('; ');
  }

  /**
   * Get base CSS styles for popup (used inside Shadow DOM)
   * These styles are completely isolated from the host page
   */
  private getPopupBaseStyles(): string {
    return `
      /* CSS Reset for popup elements */
      *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      /* Modal overlay */
      .qc-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      }

      /* Popup box (modal content) */
      .qc-popup-box {
        position: relative;
        background-color: #ffffff;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        max-width: 90vw;
        max-height: 90vh;
        overflow: auto;
      }

      /* Bar and slide-in popups */
      .qc-popup {
        position: fixed;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        background-color: #ffffff;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      }

      .qc-popup-bar {
        left: 0;
        right: 0;
      }

      .qc-popup-bar.qc-popup-top {
        top: 0;
      }

      .qc-popup-bar.qc-popup-bottom {
        bottom: 0;
      }

      .qc-popup-slide_in {
        bottom: 100px;
        border-radius: 12px;
      }

      .qc-popup-slide_in.qc-popup-left {
        left: 20px;
      }

      .qc-popup-slide_in.qc-popup-right {
        right: 20px;
      }

      /* Popup content */
      .qc-popup-content {
        position: relative;
        padding: 24px;
      }

      /* Close button */
      .qc-popup-close {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #666;
        transition: background-color 0.2s, color 0.2s;
      }

      .qc-popup-close:hover {
        background-color: rgba(0, 0, 0, 0.1);
        color: #333;
      }

      .qc-popup-close svg {
        width: 16px;
        height: 16px;
      }

      /* Popup body (HTML content area) */
      .qc-popup-body {
        margin-bottom: 16px;
        line-height: 1.5;
        color: #333;
      }

      .qc-popup-body:last-child {
        margin-bottom: 0;
      }

      /* Common content styles */
      .qc-popup-body h1, .qc-popup-body h2, .qc-popup-body h3 {
        margin-bottom: 12px;
        font-weight: 600;
        color: #111;
      }

      .qc-popup-body h1 { font-size: 24px; }
      .qc-popup-body h2 { font-size: 20px; }
      .qc-popup-body h3 { font-size: 18px; }

      .qc-popup-body p {
        margin-bottom: 12px;
        color: #555;
      }

      .qc-popup-body p:last-child {
        margin-bottom: 0;
      }

      .qc-popup-body img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
      }

      .qc-popup-body ul, .qc-popup-body ol {
        margin-left: 20px;
        margin-bottom: 12px;
      }

      .qc-popup-body li {
        margin-bottom: 4px;
      }

      /* CTA buttons container */
      .qc-popup-buttons {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }

      /* CTA button base */
      .qc-popup-btn {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .qc-popup-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .qc-popup-btn:active {
        transform: translateY(0);
      }

      .qc-popup-btn-primary {
        background-color: #2563eb;
        color: #ffffff;
      }

      .qc-popup-btn-primary:hover {
        background-color: #1d4ed8;
      }

      .qc-popup-btn-secondary {
        background-color: #f3f4f6;
        color: #374151;
      }

      .qc-popup-btn-secondary:hover {
        background-color: #e5e7eb;
      }

      /* Helper classes for user content */
      .popup-title {
        font-size: 24px;
        font-weight: 700;
        color: #111;
        margin-bottom: 8px;
      }

      .popup-description {
        font-size: 16px;
        color: #555;
        line-height: 1.6;
      }

      .popup-image {
        width: 100%;
        border-radius: 8px;
        margin-bottom: 16px;
      }
    `;
  }

  /**
   * Build popup HTML content
   */
  private buildPopupHTML(popup: PopupConfig): string {
    const ctaConfig = popup.cta_config || {};
    const primaryBtn = ctaConfig.primary_button || {};
    const secondaryBtn = ctaConfig.secondary_button || {};

    // Process HTML content - replace image placeholders
    let htmlContent = popup.html_content || '';
    const images = (popup as any).images || {};

    // Replace {{images.key}} placeholders with actual URLs
    htmlContent = htmlContent.replace(/\{\{images\.(\w+)\}\}/g, (match: string, key: string) => {
      const imageData = images[key];
      if (imageData) {
        return typeof imageData === 'string' ? imageData : imageData.url || '';
      }
      return match; // Return original if not found
    });

    let html = `
      <div class="qc-popup-content">
        <button class="qc-popup-close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <div class="qc-popup-body">
          ${htmlContent}
        </div>
    `;

    // Add CTA buttons if enabled
    if ((primaryBtn.enabled !== false) || (secondaryBtn.enabled !== false)) {
      html += '<div class="qc-popup-buttons">';

      if (primaryBtn.enabled !== false) {
        html += this.buildPopupButton(primaryBtn, 'primary');
      }
      if (secondaryBtn.enabled !== false) {
        html += this.buildPopupButton(secondaryBtn, 'secondary');
      }

      html += '</div>';
    }

    html += '</div>';

    return html;
  }

  /**
   * Build a popup button HTML
   */
  private buildPopupButton(config: Record<string, any>, type: string): string {
    const text = config.text || (type === 'primary' ? 'Get Started' : 'Maybe Later');
    const action = config.action || (type === 'primary' ? 'open_chat' : 'close');
    const style = config.style || (type === 'primary' ? 'filled' : 'outline');
    const color = config.color || (type === 'primary' ? '#4f46e5' : '#6b7280');
    const borderRadius = config.border_radius || 'md';

    const borderRadiusMap: Record<string, string> = {
      none: '0',
      sm: '4px',
      md: '8px',
      lg: '12px',
      full: '9999px',
    };

    let buttonStyles: string[] = [];
    buttonStyles.push('padding: 10px 20px');
    buttonStyles.push('font-size: 14px');
    buttonStyles.push('font-weight: 500');
    buttonStyles.push('cursor: pointer');
    buttonStyles.push('transition: all 0.2s ease');
    buttonStyles.push(`border-radius: ${borderRadiusMap[borderRadius] || '8px'}`);

    switch (style) {
      case 'filled':
        buttonStyles.push(`background-color: ${color}`);
        buttonStyles.push('color: white');
        buttonStyles.push('border: none');
        break;
      case 'outline':
        buttonStyles.push('background-color: transparent');
        buttonStyles.push(`color: ${color}`);
        buttonStyles.push(`border: 2px solid ${color}`);
        break;
      case 'ghost':
        buttonStyles.push('background-color: transparent');
        buttonStyles.push(`color: ${color}`);
        buttonStyles.push('border: none');
        break;
      case 'link':
        buttonStyles.push('background-color: transparent');
        buttonStyles.push(`color: ${color}`);
        buttonStyles.push('border: none');
        buttonStyles.push('text-decoration: underline');
        buttonStyles.push('padding: 10px 5px');
        break;
    }

    return `
      <button
        class="qc-popup-btn qc-popup-btn-${type}"
        data-action="${action}"
        data-url="${config.url || ''}"
        style="${buttonStyles.join('; ')}"
      >
        ${text}
      </button>
    `;
  }

  /**
   * Setup popup event listeners
   */
  private setupPopupEventListeners(popup: PopupConfig): void {
    if (!this.popupContainer) return;

    // Get shadow root for querying elements
    const shadow = this.popupContainer.shadowRoot;
    if (!shadow) return;

    // Close button
    const closeBtn = shadow.querySelector('.qc-popup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup('dismiss'));
    }

    // CTA buttons
    const ctaButtons = shadow.querySelectorAll('.qc-popup-btn');
    ctaButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const action = target.dataset.action;
        const url = target.dataset.url;

        this.trackPopupEvent(popup.id, 'click', action);

        switch (action) {
          case 'open_chat':
            this.closePopup('click');
            this.open(); // Open chat widget
            break;
          case 'close':
            this.closePopup('click');
            break;
          case 'open_url':
            if (url) {
              window.open(url, '_blank');
            }
            this.closePopup('click');
            break;
          case 'open_url_same':
            if (url) {
              window.location.href = url;
            }
            break;
          default:
            this.closePopup('click');
        }
      });
    });

    // Click outside to close (for modals - clicking on the overlay closes the popup)
    if (popup.type === 'modal') {
      const overlay = shadow.querySelector('.qc-popup-overlay');
      if (overlay) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            this.closePopup('dismiss');
          }
        });
      }
    }

    // ESC key to close
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.activePopup) {
        this.closePopup('dismiss');
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Close the active popup
   */
  private closePopup(reason: 'click' | 'dismiss'): void {
    if (!this.activePopup || !this.popupContainer) return;

    const popupId = this.activePopup.id;

    // Track dismissal
    if (reason === 'dismiss') {
      this.trackPopupEvent(popupId, 'dismissal');
    }

    // Remove from DOM
    this.popupContainer.remove();
    this.popupContainer = null;
    this.activePopup = null;

    this.log(`🚪 Popup ${popupId} closed (${reason})`);
  }

  /**
   * Track popup events (impression, click, dismissal)
   */
  private async trackPopupEvent(popupId: string, eventType: string, action?: string): Promise<void> {
    try {
      await fetch(`${this.config.apiUrl}/api/widget/popups/${popupId}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Widget-Key': this.config.widgetKey,
        },
        body: JSON.stringify({
          event_type: eventType,
          cta_action: action,
          page_url: window.location.href,
          visitor_id: this.guestId,
        }),
      });
      this.log(`📊 Popup event tracked: ${eventType} for ${popupId}`);
    } catch (error) {
      this.log('⚠️ Failed to track popup event:', error);
    }
  }
}
